// reusable API endpoint definitions
// assumes that axios is already available
// makes things easy to update endpoints all at once

// use this to CREATE a base instance of Axios, then call it with certain pre-configured URLs and return Promises
// Option 1:  just use base instance splAxios.get() or .post() etc... and provide your own URL (without /api/ prefix)
// Option 2:  for standard ViewSet URLs, add endpoints here, then use them like suppliosApi.product.list().then(response => {handle response}).catch(error => {handle error})

import axios from 'axios';
import ResourceViewsetEndpoint from './api/base-endpoint';  // NOTE: these will use /api/v1/...
import { getCustomFlowObjectEndpoint } from './api/customFlowObjectEndpoint';
import FlowObjectViewsetEndpoint from './api/flow-object-endpoint';
import downloadObjectGridExcel from './api/objectGridExcelExport';

const splAxios = axios.create({
  baseURL: '/api/', // pass 'url' later, which will be appended to this - don't need leading slash on what you append
  xsrfCookieName: window?.splData?.CSRF_COOKIE_NAME || 'csrftoken',
  xsrfHeaderName: 'X-CSRFTOKEN',
  headers: {
    ...(window?.splFixedHeaders || {}),
  },
});

// NOTE: make sure not to use these for working with external APIs... otherwise you'll be sending the CSRF cookie there

const splAxiosBare = axios.create({
  // use this only for URLs that don't use router (not through /api/)
  xsrfCookieName: window?.splData?.CSRF_COOKIE_NAME || 'csrftoken',
  xsrfHeaderName: 'X-CSRFTOKEN',
  headers: {
    ...(window?.splFixedHeaders || {}),
  },
});

const getAuthUrlPath = function () {
  return window.splUrlMap.auth_url;
};

// THINGS TO ADD
// -----------------------------------------------------
// 1) Session Expire stuff: Add something in your base api method that redirects user to login if session is expired, or shows toast message/overlay if expired
// overlay is probably best... with message/button for reloading current page so that current URL is not lost
// OR... just look at session cookie expiration time and show overlay if past expiration - sessionid cookie is secure (can't get from JS) so send other cookie?
// https://www.w3schools.com/js/js_cookies.asp
// 2) outdated front-end code?  in case of new deployment, with new front-end bundle but user still has old one because of SPA.
// - have a regular ping to backend asking if outdated?
// - send something in each request saying what version we're using?  backend compares, sets a header saying 'outdated', then splAxios checks that header and triggers some message to user saying 'you should reload'.
//      - seems like lots of extra overhead, might be annoying, and would probably show even if you made things backwards compatible
//      - this is one reason do to LESS SPA stuff

// constructor for generic resource
function APIEndpoint(generalUrl, responseDataName, isTaggable = false) {
  // property definitions
  this.generalUrl = generalUrl; // generalUrl is the URL for this object, with trailing slash
  this.objectUrl = function (id) {
    return this.generalUrl + id.toString() + '/'; // has resource ID + trailing slash:  like rfxs/6/
  };
  this.list = function (config) {
    return splAxios.get(this.generalUrl, config);
  };
  this.listRelated = function (id, relatedUrl, config) {
    // pass a string of a child/related URL to get related/child objects, using D-REST ViewSet
    // like for product attachments, use product endpoint and pass 'attachments/' as relatedUrl
    return splAxios.get(this.objectUrl(id) + relatedUrl, config);
  };
  this.retrieve = function (id, config) {
    return splAxios.get(this.objectUrl(id), config);
  };
  this.create = function (data, config) {
    return splAxios.post(this.generalUrl, data, config);
  };
  this.update = function (id, data, config) {
    return splAxios.put(this.objectUrl(id), data, config);
  };
  this.partialUpdate = function (id, data, config) {
    return splAxios.patch(this.objectUrl(id), data, config);
  };
  this.remove = function (id, config) {
    // ONLY use for things with is_active flag - this means you have to keep entire partial_update method open.. instead, you should implement a custom @action that ONLY does soft delete, and call that action from this
    return splAxios.patch(this.objectUrl(id), { is_active: false }, config);
  };
  this.destroy = function (id, config) {
    return splAxios.delete(this.objectUrl(id), config);
  };
  this.head = function (config) {
    return splAxios.head(this.generalUrl, config);
  };
  this.options = function (config) {
    return splAxios.options(this.generalUrl, config);
  };
  if (isTaggable) {
    this.update_spl_taggings = function (id, tagData, config) {
      return splAxios.post(this.objectUrl(id) + 'update_spl_taggings/', tagData, config);
    };
    this.get_spl_taggings = function (id, config) {
      return splAxios.get(this.objectUrl(id) + 'get_spl_taggings/', config);
    };
    this.get_available_spl_tags_for_field = function (id, fieldId, config) {
      const axiosConfig = config || {};
      const params = axiosConfig['params'] ?? {};
      params['tag_field'] = fieldId;
      axiosConfig['params'] = params;
      return splAxios.get(this.objectUrl(id) + 'get_available_spl_tags_for_field/', axiosConfig);
    };
  }
  this.responseDataName = responseDataName; // string of name for this resource on response.data.______ when used with DREST serializers
}

const standardActions = ['list', 'retrieve', 'create', 'update', 'partialUpdate', 'remove', 'destroy', 'head', 'options'];

const dummyAction = function (actionName) {
  let errMsg = `This action (${actionName}) is not implemented on this endpoint (generalUrl = ${this.generalUrl})`;
  console.error(errMsg);
  return Promise.reject(errMsg);
}; // then when using this in constructor of ViewsetEndpoint, use bind, with thisArg being the viewset class, and first arg being actionName

class GenericViewsetEndpoint extends APIEndpoint {
  constructor({
    generalUrl,
    enabledActions = standardActions, // array of the standard actions that you want enabled - if you omit this altogether from constructor params, then all will be enabled
  }) {
    super(generalUrl); // call the super class constructor
    // now run through enabledMethods and override ones that are not enabled
    let disabledActions = standardActions.filter((actionName) => !enabledActions.includes(actionName));
    disabledActions.forEach((disabledActionName) => {
      this[disabledActionName] = dummyAction.bind(this, disabledActionName);
    });
  }
}

let rfxEndpoint = new GenericViewsetEndpoint({
  generalUrl: 'rfxs/',
  enabledActions: ['list'],
});
rfxEndpoint.getResponses = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_responses/', config);
};
let rfxResponseEndpoint = new GenericViewsetEndpoint({
  generalUrl: 'rfx-responses/',
  enabledActions: ['retrieve'],
});

const suppliosApiNew = {
  rfx: rfxEndpoint,
  rfxResponse: rfxResponseEndpoint,
};
// yes, all of the above works.  is it better?  maybe

// ADD NEW ENDPOINTS HERE:
// first arg:  the endpoint URL - no leading slash, but should have trailing slash
// second arg:  responseDataName - PLURAL name of resource in response.data.---- as defined in your serializer (but pluralized)
const suppliosApi = {
  productCategory: new APIEndpoint('product-categories/'), // non D-REST new in Aug 2020
  //product: new APIEndpoint('dyn-items/', 'products'),
  item: new APIEndpoint('items/'), // non-D-REST ProductViewSet, new in Aug 2020
  companyProduct: new GenericViewsetEndpoint({ generalUrl: 'company-products/', enabledActions: [] }), // NO standard actions, only ObjectGrid endpoints
  rfx: new APIEndpoint('rfxs/'), // non-D-REST ViewSet endpoint
  sourcingRequest: new APIEndpoint('sourcing-request/'),
  sourcingRequestFlow: new FlowObjectViewsetEndpoint({ modelClass: 'SourcingRequest', generalUrl: 'sourcing-request/' }),
  rfxResponse: new APIEndpoint('rfx-responses/'), // non-D-REST ViewSet -- used for response management page though
  //rfxVendorResponse: new APIEndpoint('rfx-vendor-responses/', 'rfx_response'),  // D-REST ViewSet
  rfxResponseScoreSheet: new APIEndpoint('rfx-response-score-sheets/'),
  rfxCriteriaScore: new APIEndpoint('rfx-criteria-scores/', 'rfx_criteria_scores'),
  rfxBulkMessage: new APIEndpoint('rfx-bulk-messages/', 'rfx_bulk_messages'),
  sourcingCategory: new APIEndpoint('sourcing-categories/'),
  sourcingTag: new APIEndpoint('sourcing-tags/'),
  vendorStandard: new APIEndpoint('vendors/', undefined, true), // NON D-REST ViewSet for Company objs - VendorViewSet - # isTaggable = true
  //companyTag: new APIEndpoint('company-tags/'),  // for MANAGING (and listing) tags themselves, NOT taggings
  //companyTagCategory: new APIEndpoint('company-tag-categories/'),  // for MANAGING (and listing) the tag CATEGORIES
  companyTag: new ResourceViewsetEndpoint({ resourceName: 'CompanyTag', generalUrl: 'company-tags/' }), // all actions enabled for now
  companyTagCategory: new ResourceViewsetEndpoint({ resourceName: 'CompanyTagCategory', generalUrl: 'company-tag-categories/' }), // all actions enabled for now
  companyLocation: new APIEndpoint('company-locations/'), // regular ModelViewSet for Company Locations
  companySite: new APIEndpoint('company-sites/', undefined, true), // isTaggable (update_spl_taggings, etc.) -- CompanySiteViewSet - only for tenants with custom_flags.use_vendor_sites (replaces companyLocation there)
  companySiteType: new APIEndpoint('company-site-types/'), // CompanySiteTypeViewSet - only for tenants with custom_flags.use_vendor_sites
  contact: new APIEndpoint('contacts/'), // uses TenantUserViewSet as contacts - non D-REST endpoint
  internalTu: new APIEndpoint('internal-users/'), // uses InternalTenantUserViewSet - non D-REST endpoint just for working with INTERNAL TUs
  tuEmailIssues: new GenericViewsetEndpoint({ generalUrl: 'tu-email-issues/', enabledActions: [] }), // uses TenantUserEmailIssueViewSet - just for working with TU.email_issues_since
  tuRoles: new GenericViewsetEndpoint({ generalUrl: 'tu-roles/', enabledActions: ['list'] }), // uses TenantUserRoleViewSet - just for listing roles
  tuNotes: new GenericViewsetEndpoint({ generalUrl: 'tu-notes/', enabledActions: [] }), // NO DEFAULT ACTIONS - the entity is TU here, but all actions are for working with notes on those TUs
  messageThread: new APIEndpoint('message-threads/'), // NON D-REST ViewSet for MessageThread objects
  coreMessage: new APIEndpoint('core-messages/'), // NON D-REST ViewSet for CoreMessage objects
  docRequest: new APIEndpoint('docrequests/'), // non d-rest ViewSet for DocRequest
  docRequestReply: new APIEndpoint('docrequest-replies/'), // non d-rest ViewSet for DocRequestReply
  formiumForm: new APIEndpoint('formium-forms/'), // non d-rest full ViewSet for non-RFX related FormiumForm
  formiumReply: new APIEndpoint('formium-replies/'), // non d-rest READ-ONLY ViewSet for non-RFX related FormiumReply
  quoteSheet: new APIEndpoint('quotesheets/'), // non d-rest ViewSet for QuoteSheet objs
  quoteSheetReply: new APIEndpoint('quotesheet-replies/'), // non d-rest ViewSet for QuoteSheetReply objects
  //rfxCompanyToDo: new APIEndpoint('rfx-company-todos/'),  // non d-rest endpoint for RfxCompanyToDo - also have some related endpoints on rfx ViewSet
  rfxCompanyToDo: new GenericViewsetEndpoint({ generalUrl: 'rfx-company-todos/', enabledActions: ['retrieve'] }),
  // Quality / DemoWorkflow stuff
  workflowSpec: new GenericViewsetEndpoint({ generalUrl: 'demo-workflow-specs/', enabledActions: [] }),
  //workflowStep: new GenericViewsetEndpoint({generalUrl: 'demo-workflow-steps/', enabledActions: []}),  // NO enabledActions for now -- can add them as needed
  //workflowTaskSpec: new GenericViewsetEndpoint({generalUrl: 'demo-workflow-task-specs/', enabledActions: []}),  // NO enabledActions for now -- can add them as needed
  //workflowForm: new GenericViewsetEndpoint({generalUrl: 'demo-workflow-forms/', enabledActions: []}),  // NO enabledActions for now -- can add them as needed
  workflowFormVersion: new GenericViewsetEndpoint({ generalUrl: 'demo-workflow-form-versions/', enabledActions: [] }), // NO enabledActions for now -- can add them as needed
  workflowRun: new GenericViewsetEndpoint({ generalUrl: 'demo-workflow-runs/', enabledActions: [] }), // removed all, use specific ones - use get_list_internal or get_list_external
  workflowTask: new GenericViewsetEndpoint({ generalUrl: 'demo-workflow-tasks/', enabledActions: [] }), // use other methods for create, update, etc.
  qualityAnalytics: new GenericViewsetEndpoint({ generalUrl: 'quality-analytics/', enabledActions: ['list'] }), // NO enabledActions -- only custom actions
  qualityIssue: new FlowObjectViewsetEndpoint({ modelClass: 'QualityIssue', generalUrl: 'quality-issues/' }),
  onboarding: new FlowObjectViewsetEndpoint({ modelClass: 'Onboarding', generalUrl: 'onboarding/' }),
  // COMPLIANCE
  compItemFlow: new FlowObjectViewsetEndpoint({ modelClass: 'ComplianceItem', generalUrl: 'comp-item-flow/' }),
  companySiteFlow: new FlowObjectViewsetEndpoint({ modelClass: 'CompanySite', generalUrl: 'company-site-flow/' }), // FlowObject endpoints (transitions) for CompanySite -- CRUD stays on companySite
  // compSubFlow: new FlowObjectViewsetEndpoint({ modelClass: 'ComplianceSubmission', generalUrl: 'comp-sub-flow/' }),  // MOVED TO customFlowObjectEndpoint (to handle subtypes!)
  compItemStd: new GenericViewsetEndpoint({ generalUrl: 'comp-item-std/', enabledActions: [] }), // STANDARD viewset, no default actions
  compSubStd: new GenericViewsetEndpoint({ generalUrl: 'comp-sub-std/', enabledActions: [] }), // STANDARD viewset, no default actions
  compReqFlow: new FlowObjectViewsetEndpoint({ modelClass: 'ComplianceRequirement', generalUrl: 'comp-req-flow/' }),
  compItemTypeFlow: new FlowObjectViewsetEndpoint({ modelClass: 'ComplianceItemType', generalUrl: 'comp-item-type-flow/' }),
  vendorCompItemFlow: new FlowObjectViewsetEndpoint({ modelClass: 'VendorCompItem', generalUrl: 'vendor-comp-item-flow/' }),
  vendorCompItemStd: new GenericViewsetEndpoint({ generalUrl: 'vendor-comp-item-std/', enabledActions: [] }), // STANDARD viewset, no default actions
  compEas: new GenericViewsetEndpoint({ generalUrl: 'comp-eas/', enabledActions: [] }), // STANDARD viewset, no default actions
  // PPAP
  ppapRun: new FlowObjectViewsetEndpoint({ modelClass: 'PpapRun', generalUrl: 'ppap-runs/' }),
  purchaseOrder: new FlowObjectViewsetEndpoint({ modelClass: 'PurchaseOrder', generalUrl: 'pos/' }),
  // CANNOT USE HERE - need to get it dynamically with subtype: supplierProject: new FlowObjectViewsetEndpoint({ modelClass: 'SupplierProject', generalUrl: 'supplier-projects/' }),
  // PERFORMANCE 
  performanceSystem: new FlowObjectViewsetEndpoint({ modelClass: 'PerformanceSystem', generalUrl: 'performance-systems/' }),
  // CustomFlowObject
  // USE SPECIAL CONSTRUCTOR INSTEAD customFlowObject: new FlowObjectViewsetEndpoint({ modelClass: 'CustomFlowObjectModel', generalUrl: 'custom-flow-objects/' }),
  // SCORECARDS - also in 'quality'
  scorecardSystem: new GenericViewsetEndpoint({ generalUrl: 'scorecard-systems/', enabledActions: [] }), // NO enabledActions -- only custom actions
  // SMARTFORMS - SmartFormTemplate, SmartForm, SmartFormReply - additional actions below also
  smartFormTemplate: new GenericViewsetEndpoint({ generalUrl: 'smartform-templates/', enabledActions: ['create', 'list'] }),
  smartForm: new GenericViewsetEndpoint({ generalUrl: 'smartforms/', enabledActions: ['create', 'destroy'] }),
  smartFormReply: new GenericViewsetEndpoint({ generalUrl: 'smartform-replies/', enabledActions: ['destroy'] }),
  // TenantData / Tenant custom data - should you combine this with orgData?
  tenantData: new GenericViewsetEndpoint({ generalUrl: 'tenant-custom-data/', enabledActions: ['list', 'retrieve', 'update', 'partialUpdate'] }),
  // GeneralTenantFile  (in custom_data app)
  generalTenantFile: new GenericViewsetEndpoint({ generalUrl: 'general-tenant-files/', enabledActions: [] }), // no standard actions - use get_by_slug() for now
  // DocHub - DocHubResource, DocHubResourceVersion, DocHubFile, etc...
  dochubResource: new GenericViewsetEndpoint({ generalUrl: 'dochub-resources/' }), // all actions enabled for now, but have perms/queryset controlling things
  dochubResourceVersion: new GenericViewsetEndpoint({ generalUrl: 'dochub-resource-versions/' }), // all actions for now
  dochubGroupManage: new GenericViewsetEndpoint({ generalUrl: 'dochub-group-manage/' }), // all actions for now
  // SNIPPETS - using new ResourceViewsetEndpoint
  snippetArea: new ResourceViewsetEndpoint({ resourceName: 'SnippetArea', generalUrl: 'snippet-areas/' }), // LOOKUP BY SLUG, NOT ID - only list/retrieve are available to users - others are supplios staff only
  snippet: new ResourceViewsetEndpoint({ resourceName: 'Snippet', generalUrl: 'snippets/' }), // all actions enabled for now

  rfxAward: new FlowObjectViewsetEndpoint({ modelClass: 'RfxAward', generalUrl: 'rfx-awards/' }),

  // SINGLE TENANT CONFIG - for Tenant Settings and Feature Flags
  tenantConfig: new GenericViewsetEndpoint({ generalUrl: 'tenant-config/', enabledActions: [] }),  /// TODO TODO TODO 

  // COMPANY PROFILE DATA ENDPOINT - for related data on company, updateable by external users
  companyProfileData: new GenericViewsetEndpoint({ generalUrl: 'company-profile-data/', enabledActions: [] }), // only extra actions

  // i18n endpoints
  i18n: {
    listLanguages: function (config) {
      return splAxiosBare.get('/i18n/change/', config);
    },
    changeLanguage: function (langCode, config) {
      return splAxiosBare.post('/i18n/change/', { language: langCode }, config);
    },
  },

  //
  orgData: {
    getCurrencies: function (config) {
      // this fetches active currencies for the Tenant org
      return splAxiosBare.get('/orgs/currencies/', config);
    },
    /* tenantUserList: function(config) {
            return splAxiosBare.get('/orgs/tenant-user-list/', config);
        }, */ // replace with real TU ViewSet
    internalUserList: function (config) {
      return splAxiosBare.get('/orgs/internal-user-list/', config);
    },
    /* tuRfxManageList: function(config) {  // probably move this out of orgData grouping later, if you move it in django url structure
            return splAxiosBare.get('/orgs/tu-endpoints/rfx-manage-list/', config);
        }, */
    licenseTypeCounts: function (config) {
      // gets a dict/obj with license type counts and allowed counts
      return splAxiosBare.get('/orgs/license-type-counts/', config);
    },
  },
  emailIssuesSettings: {
    retrieve: function (config) {
      return splAxiosBare.get('/emailer/api/email-issues-settings/', config);
    },
    update: function (data, config) {
      // supports full or partial updates, but only what is included in the serializer
      return splAxiosBare.patch('/emailer/api/email-issues-settings/', data, config);
    },
    getReportRecipientsOptions: function (config) {
      return splAxiosBare.get('/emailer/api/email-issues-settings/report-recipients-options/', config);
    },
  },
  internal: {
    vendorSearch: function (searchString, config) {
      // returns list of vendors that match search string
      let queryString = searchString ? '?search=' + searchString : '';
      return splAxiosBare.get('/api-views/internal/vendor-search/' + queryString, config);
    },
    vendorComplexSearch: function (name, config) {
      let queryString = '?name__icontains=' + name; // REMOVED ADDRESS SEARCH + '&address__icontains=' + address;
      return splAxiosBare.get('/api-views/internal/vendor-complex-search/' + queryString, config);
    },
    testAnalyticsViewGet: function (config) {
      return splAxiosBare.get('/api-views/internal/test-analytics-view/', config);
    },
    testAnalyticsViewPost: function (data, config) {
      return splAxiosBare.post('/api-views/internal/test-analytics-view/', data, config);
    },
  },
  files: {
    // NOTE - Dec 3 2021 - you made new versions of these, put everything together in file-helpers or some other file service thingy - keeping these to support older versions
    getS3UploadUrl: function (file_category, filename, filetype, otherData, config) {
      // file category (class), filename, filetype to get object needed for direct-to-S3 POST upload
      let postData = { file_category: file_category, filename: filename, filetype: filetype, ...otherData };
      return splAxiosBare.post('/core-files/get-s3-upload-url/', postData, config);
    },
    saveS3Upload: function (fileData, config) {
      // fileData should contain all the fields you API endpoint is expecting, in the correct format & defaults for empty values
      return splAxiosBare.post('/core-files/save-s3-upload/', fileData, config);
    },
    deleteS3Upload: function (file_category, file_id, config) {
      let postData = { file_category: file_category, id: file_id };
      return splAxiosBare.post('/core-files/delete-s3-upload/', postData, config);
    },
    getS3FileData: function (file_category, file_id, config) {
      // must add file_category an file_id (as 'id') to query params - this allows passing in other params as well
      const finalConfig = config ?? {};
      const params = finalConfig.params ?? {};
      params['id'] = file_id;
      params['category'] = file_category;
      finalConfig.params = params;
      return splAxiosBare.get('/core-files/get-s3-file-data/', finalConfig);
    },
    getS3FilesMeta: function (files, config) {
      // BULK metadata (no download URLs) for many files at once - files is an array of {category, id}
      // use loadS3FileMeta() in components/s3_files/s3_file_meta.js instead of calling this directly - it batches for you
      return splAxiosBare.post('/core-files/get-s3-files-meta/', { files }, config);
    },
    getFileBundleUrl: function ({ category, ids, filename = '' }) {
      // file_ids should be an array of IDs
      const axiosConfig = {
        params: {
          category: category,
          ids: Array.isArray(ids) ? [...ids] : [ids],
        },
      };
      if (filename) {
        axiosConfig.params.filename = filename;
      }
      return splAxiosBare.get('/core-files/get-zip-bundle-url/', axiosConfig);
    },
  },
  locations: {
    baseUrl: '/locations/api/',
    loadAddressFormData: function (config) {
      // loads metadata for the address form
      return splAxiosBare.get(suppliosApi.locations.baseUrl + 'load-address-form-data/', config);
    },
    addressQuery: function (queryData, config) {
      return splAxiosBare.post(suppliosApi.locations.baseUrl + 'address-query/', queryData, config);
    },
    createTestLocation: function (locationData, config) {
      return splAxiosBare.post(suppliosApi.locations.baseUrl + 'create-test-location/', locationData, config);
    },
  },
  auditLogging: {
    async getAdminInformation() {
      const response = await splAxios.get('/audit_logging/get_admin_info/');
      return response.data;
    },
    async getAuditLogs(startDate, endDate, events) {
      const response = await splAxios.get(`/audit_logging/get_audit_logs/?start_date=${startDate}&end_date=${endDate}&events=${events.join(',')}`);
      return response.data;
    },
  },
  phoneNumbers: {
    /**
     * Gets the phone numbers for a given tu
     * @param {string} tenantUserId
     * @param {*} config
     * @returns
     */
    async getPhoneNumbersForTu(tenantUserId, config) {
      return splAxiosBare.get(`/orgs/tu-endpoints/${tenantUserId}/phone-number/`, config);
    },
    /**
     * Saved the tu's phone numbers
     * @param {string} tenantUserId
     * @param {object} data
     * @param {object} config
     * @returns
     */
    async updatePhoneNumbersForTu(tenantUserId, data, config) {
      return splAxiosBare.patch(`/orgs/tu-endpoints/${tenantUserId}/phone-number/`, data, config);
    },
  },
  auth: {
    login: function (loginData, config) {
      const loginFormData = new FormData();
      loginFormData.append('login', loginData.login || '');
      loginFormData.append('password', loginData.password || '');
      loginFormData.append('csrfmiddlewaretoken', loginData.csrfmiddlewaretoken || '');
      loginFormData.append('next', loginData.next || '');
      const headers = {
        // csrftoken is already part of splAxiosBare
        ...(config?.headers || {}),
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'HTTP-ACCEPT': 'application/json',
      };
      return splAxiosBare.post('/accounts/login/', loginFormData, {
        ...config,
        headers,
      });
    },
    logout: function (config) {
      let logoutUrl = '/accounts/logout/';
      let logoutConfig = {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }; // we need to set this header, or content-type, or ACCEPT header to "application/json" (only) so allauth knows this is an AJAX POST request and it sends back the appropriate response
      let finalConfig = Object.assign({}, logoutConfig, config); // merge everything, although you most likely won't be passing in any config object
      return splAxiosBare.post(logoutUrl, null, finalConfig);
      // this will return a JSON object with URL to redirect to, as per your django & allauth settings {location: '/the-appropriate-redirect-url/'}
    },
    async getUserAdminAuthenticationInformation(userId) {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-user-admin-authentication-information/${userId}/`);
        return response.data;
      } catch (error) {
        console.error('error happened while loading the user information', error);
        return {};
      }
    },
    async getAuthenticationAdminInformation() {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-admin-information/`);
        return response.data;
      } catch (error) {
        console.error('error happened while getting login_methods for admin', error);
        return [];
      }
    },
    async connectTuToCu(tenantUserId) {
      try {
        await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/connect-tu-to-cu/${tenantUserId}/`);
        return true;
      } catch (error) {
        console.error('error happened while connecting to to cu', error);
        return false;
      }
    },
    async getLoginMethods(tenantId = null) {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-login-methods/${tenantId ? tenantId + '/' : ''}`);
        if (response.data) {
          return response.data;
        }
        throw new Error('authentication_methods not found');
      } catch (error) {
        console.error('error happened while getting login_methods', error);
        return [];
      }
    },
    async disconnectLoginMethod(tenantUserId, loginMethodId) {
      await splAxiosBare.delete(`/accounts/${getAuthUrlPath()}/disconnect-login-method/${tenantUserId}/${loginMethodId}/`);
    },
    async getTenantUserAuthenticationInformation(tenant_user_id) {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-authentication-information/${tenant_user_id}/`);
        if (response.data && response.data) {
          return response.data;
        }
        throw new Error('identities not found');
      } catch (error) {
        console.error('error happened while getting identities', error);
        return [];
      }
    },
    async getAuthenticationInformation() {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-authentication-information/`);
        if (response.data && response.data) {
          return response.data;
        }
        throw new Error('identities not found');
      } catch (error) {
        console.error('error happened while getting identities', error);
        return [];
      }
    },
    async getPossibleActiveTus(tenantId) {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-authentication-information/tenant/${tenantId}/`);
        if (response.data && response.data) {
          return response.data;
        }
        throw new Error('possible tus not found');
      } catch (error) {
        console.error('error happened while getting possible active tus', error);
        return [];
      }
    },
    async resendVerificationEmail(vkey) {
      try {
        const response = await splAxiosBare.post(`/accounts/${getAuthUrlPath()}/resend-email-verification-code/${vkey}/`);
        if (response.status === 201) {
          return;
        }
        throw new Error('error resending verification code email');
      } catch (error) {
        console.error('error happened while resending verification code email', error);
        throw error;
      }
    },
    async getTenantUserMismatchData(newTuId, oldTuId) {
      try {
        const response = await splAxiosBare.get(`/accounts/${getAuthUrlPath()}/get-tu-mismatch/` + newTuId + '/' + oldTuId + '/');
        if (response.status === 200) {
          return response.data;
        }
        throw new Error('error getting tu user data');
      } catch (error) {
        console.error(`error happened while getting tu user data newTuId: ${newTuId} oldTuId ${oldTuId}`, error);
        throw error;
      }
    },
  },
  portal: {
    baseUrl: '/portal/api/',
    getProfileUpdate: function (config) {
      return splAxiosBare.get(suppliosApi.portal.baseUrl + 'profile-update/', config);
    },
    updateProfileUpdate: function (profileData, config) {
      return splAxiosBare.put(suppliosApi.portal.baseUrl + 'profile-update/', profileData, config);
    },
    getCompanyProfile: function (config) {
      return splAxiosBare.get(suppliosApi.portal.baseUrl + 'company-profile-update/', config);
    },
    updateCompanyProfile: function (companyData, config) {
      return splAxiosBare.put(suppliosApi.portal.baseUrl + 'company-profile-update/', companyData, config);
    },
    getCompanyUserList: function (config) {
      return splAxiosBare.get(suppliosApi.portal.baseUrl + 'company-user-list/', config);
    },
    companyUserCreate: function (userData, config) {
      return splAxiosBare.post(suppliosApi.portal.baseUrl + 'company-user-create/', userData, config);
    },
    companyUserUpdate: function (tuId, userData, config) {
      return splAxiosBare.put(suppliosApi.portal.baseUrl + 'company-user-update/' + tuId + '/', userData, config);
    },
    companyUserReminder: function (tuId, config) {
      let tuData = { invitee: tuId };
      return splAxiosBare.post(suppliosApi.portal.baseUrl + 'company-user-reminder/', tuData, config);
    },
    companyUserRemoveAccess: function (tuId, config) {
      let tuData = { invitee: tuId };
      return splAxiosBare.post(suppliosApi.portal.baseUrl + 'company-user-remove-access/', tuData, config);
    },
    companyUserDeactivate: function (tuId, config) {
      let tuData = { invitee: tuId };
      return splAxiosBare.post(suppliosApi.portal.baseUrl + 'company-user-deactivate/', tuData, config);
    },
  },
  dashboard: {
    // in dashboard app, the api views
    baseUrl: '/dashboard-api/',
    tu_active_tasks: function (config) {
      return splAxiosBare.get(suppliosApi.dashboard.baseUrl + 'tu_active_tasks/', config);
    },
    tu_active_quality_issues: function (config) {
      return splAxiosBare.get(suppliosApi.dashboard.baseUrl + 'tu_active_quality_issues/', config);
    },
  },
  workflowForms: {
    // NOTE - you also have viewset at workflowForm (no s)
    getTestForm: function (config) {
      return splAxiosBare.get('/quality/api/multi_page_form/', config);
    },
    postTestForm: function (postData, config) {
      return splAxiosBare.post('/quality/api/multi_page_form/', postData, config);
    },
    getTestFormioForm: function (config) {
      return splAxiosBare.get('/quality/api/test_formio_form/', config);
    },
    postTestFormioForm: function (postData, config) {
      return splAxiosBare.post('/quality/api/test_formio_form/', postData, config);
    },
  },
  pages: {}, // had things here under '/pages_api/' - but was only temporary sandbox stuff
  tags: {
    tag: {
      get() {
        return splAxios.get(`tags/`);
      },
      getById(id) {
        return splAxios.get(`tags/${id}/`);
      },
      getByGroup(groupId) {
        return splAxios.get(`tags/?group=${groupId}`);
      },
      create(data) {
        return splAxios.post(`tags/`, data);
      },
      update(id, data) {
        return splAxios.patch(`tags/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`tags/${id}/`);
      },
    },
    group: {
      get() {
        return splAxios.get(`tag-groups/`);
      },
      getById(id) {
        return splAxios.get(`tag-groups/${id}/`);
      },
      create(data) {
        return splAxios.post(`tag-groups/`, data);
      },
      update(id, data) {
        return splAxios.patch(`tag-groups/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`tag-groups/${id}/`);
      },
      metadata: {
        getPermissions() {
          return splAxios.get(`tag-groups/metadata_get_permissions/`);
        },
      },
    },
    groupPerm: {
      get() {
        return splAxios.get(`tag-group-perms/`);
      },
      getById(id) {
        return splAxios.get(`tag-group-perms/${id}/`);
      },
      getByGroup(groupId) {
        return splAxios.get(`tag-group-perms/?group=${groupId}`);
      },
      create(data) {
        return splAxios.post(`tag-group-perms/`, data);
      },
      update(id, data) {
        return splAxios.patch(`tag-group-perms/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`tag-group-perms/${id}/`);
      },
    },
    taggableEntity: {
      // THIS IS GONE -- need to remove from TagGroupManager also...
      get() {
        return splAxios.get(`taggable-entities/`);
      },
      getById(id) {
        return splAxios.get(`taggable-entities/${id}/`);
      },
      create(data) {
        return splAxios.post(`taggable-entities/`, data);
      },
      update(id, data) {
        return splAxios.patch(`taggable-entities/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`taggable-entities/${id}/`);
      },
    },
    tagField: {
      get() {
        return splAxios.get(`tag-fields/`);
      },
      getById(id) {
        return splAxios.get(`tag-fields/${id}/`);
      },
      getByGroup(groupId) {
        return splAxios.get(`tag-fields/?group=${groupId}`);
      },
      getBySlug(slug) {
        return splAxios.get(`tag-fields/?slug=${slug}`);
      },
      getByAppLabelAndModelName(appLabel, modelName) {
        return splAxios.get(`tag-fields/?content_type__app_label=${appLabel}&content_type__model_name=${modelName}`);
      },
      create(data) {
        return splAxios.post(`tag-fields/`, data);
      },
      update(id, data) {
        return splAxios.patch(`tag-fields/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`tag-fields/${id}/`);
      },
      list_for_model_label(modelLabel) {
        // modelLabel is something like 'items.Product' -- should have it in serialized object from backend
        return splAxios.get(`tag-fields/list_for_model_label/?model_label=${modelLabel}`);
      },
      get_available_tags(id, objectId) {
        const baseUrl = `tag-fields/${id}/get_available_tags/`;
        const fullUrl = objectId ? `${baseUrl}?object_id=${objectId}` : baseUrl;
        return splAxios.get(fullUrl);
      },
      metadata: {
        getTaggableContentTypeLookup() {
          return splAxios.get(`tag-fields/metadata_taggable_content_types/`);
        },
        getValueTypeLookup() {
          return splAxios.get(`tag-fields/metadata_value_types/`);
        },
        getLogicTypeLookup() {
          return splAxios.get(`tag-fields/metadata_logic_types/`);
        },
      },
    },
    tagFieldPerm: {
      get() {
        return splAxios.get(`tag-field-perms/`);
      },
      getByTagField(tagFieldId) {
        return splAxios.get(`tag-field-perms/?tag_field=${tagFieldId}`);
      },
      getById(id) {
        return splAxios.get(`tag-field-perms/${id}/`);
      },
      create(data) {
        return splAxios.post(`tag-field-perms/`, data);
      },
      update(id, data) {
        return splAxios.patch(`tag-field-perms/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`tag-field-perms/${id}/`);
      },
      metadata: {
        getPermissions() {
          return splAxios.get(`tag-field-perms/metadata_get_permissions/`);
        },
      },
    },
    tagFieldLocation: {
      get() {
        return splAxios.get(`tag-field-locations/`);
      },
      getByLocationSlug(slug) {
        return splAxios.get(`tag-field-locations/?fixed_location_key=${slug}`);
      },
      getByTagField(id) {
        return splAxios.get(`tag-field-locations/?tag_field=${id}`);
      },
      getById(id) {
        return splAxios.get(`tag-field-locations/${id}/`);
      },
      create(data) {
        return splAxios.post(`tag-field-locations/`, data);
      },
      update(id, data) {
        return splAxios.patch(`tag-field-locations/${id}/`, data);
      },
      delete(id) {
        return splAxios.delete(`tag-field-locations/${id}/`);
      },
      metadata: {
        getLocations() {
          return splAxios.get(`tag-field-locations/metadata_get_locations/`);
        },
        getLocationsByTagField(id) {
          return splAxios.get(`tag-field-locations/metadata_get_locations/?tag_field=${id}`);
        },
      },
    },
  },
};

// ADD EXTRA ENDPOINTS HERE for @action endpoints and such
// TenantData -- combine this with orgData???
// these are at /api/tenant-custom-data/
suppliosApi.tenantData.get_data = function (tenantDataId, config) {
  return splAxios.get(this.objectUrl(tenantDataId) + 'get_data/', config);
};
suppliosApi.tenantData.get_data_by_slug = function (slug, config) {
  return splAxios.get(this.generalUrl + `get_data_by_slug/?slug=${slug}`, config);
};
// GeneralTenantFile
suppliosApi.generalTenantFile.get_by_slug = function (slug, config) {
  const axiosConfig = config ?? {};
  const params = axiosConfig['params'] ?? {};
  params['slug'] = slug;
  axiosConfig['params'] = params;
  return splAxios.get(this.generalUrl + 'get_by_slug/', axiosConfig);
};
// DemoWorkflow / Quality - workflowSpec / workflowRun
// workflowSpec = DemoWorkflowSpec
suppliosApi.workflowSpec.list_for_create = function (config) {
  return splAxios.get(this.generalUrl + 'list_for_create/', config);
};
suppliosApi.workflowSpec.get_create_schema = function (specId, config) {
  return splAxios.get(this.objectUrl(specId) + 'get_create_schema/', config);
};
suppliosApi.workflowSpec.create_run = function (specId, data, config) {
  return splAxios.post(this.objectUrl(specId) + 'create_run/', data, config);
};
// workflowRun = DemoWorkflowRun
suppliosApi.workflowRun.get_list_internal = function (config) {
  return splAxios.get(this.generalUrl + 'get_list_internal/', config);
};
suppliosApi.workflowRun.get_list_external = function (config) {
  return splAxios.get(this.generalUrl + 'get_list_external/', config);
};
suppliosApi.workflowRun.get_overview = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_overview/', config);
};
suppliosApi.workflowRun.get_detail = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_detail/', config);
};
suppliosApi.workflowRun.get_workflow = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_workflow/', config);
};
suppliosApi.workflowRun.get_tasks = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_tasks/', config);
};
suppliosApi.workflowRun.get_workflow_run_setup_options = function (config) {
  return splAxios.get(this.generalUrl + 'get_workflow_run_setup_options/', config);
};
suppliosApi.workflowRun.create_new = function (postData, config) {
  return splAxios.post(this.generalUrl + 'create_new/', postData, config);
};
suppliosApi.workflowRun.update_instance = function (runId, updateData, config) {
  // no metadata here (for now)
  return splAxios.put(this.objectUrl(runId) + 'update_instance/', updateData, config);
};
suppliosApi.workflowRun.partial_update_instance = function (runId, updateData, config) {
  // no metadata here (for now) - and ONLY include the fields you want to update (partial update, PATCH)
  return splAxios.patch(this.objectUrl(runId) + 'partial_update_instance/', updateData, config);
};
suppliosApi.workflowRun.get_options_for_update = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_options_for_update/', config);
};
suppliosApi.workflowRun.get_vendor_member_options = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_vendor_member_options/', config);
};
suppliosApi.workflowRun.get_vendor_options = function (config) {
  // not a detail view
  return splAxios.get(this.generalUrl + 'get_vendor_options/', config);
};
suppliosApi.workflowRun.get_metadata_with_schema = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_metadata_with_schema/', config);
};
suppliosApi.workflowRun.call_transition = function (runId, postData, config) {
  return splAxios.post(this.objectUrl(runId) + 'call_transition/', postData, config);
};
suppliosApi.workflowRun.get_possible_actions = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_possible_actions/', config);
};
suppliosApi.workflowRun.call_action = function (runId, postData, config) {
  return splAxios.post(this.objectUrl(runId) + 'call_action/', postData, config);
};
suppliosApi.workflowRun.get_event_records = function (runId, config) {
  return splAxios.get(this.objectUrl(runId) + 'get_event_records/', config);
};
// WorkflowTask
suppliosApi.workflowTask.get_detail = function (taskId, config) {
  return splAxios.get(this.objectUrl(taskId) + 'get_detail/', config);
};
suppliosApi.workflowTask.update_task_setup = function (taskId, updateData, config) {
  return splAxios.put(this.objectUrl(taskId) + 'update_task_setup/', updateData, config);
};
suppliosApi.workflowTask.report_task_viewed = function (taskId, data, config) {
  return splAxios.post(this.objectUrl(taskId) + 'report_task_viewed/', data, config);
};
suppliosApi.workflowTask.submit_complete_form = function (taskId, submitData, config) {
  return splAxios.post(this.objectUrl(taskId) + 'submit_complete_form/', submitData, config);
};
suppliosApi.workflowTask.submit_partial_form = function (taskId, submitData, config) {
  return splAxios.post(this.objectUrl(taskId) + 'submit_partial_form/', submitData, config);
};
suppliosApi.workflowTask.submit_draft = function (taskId, submitData, config) {
  return splAxios.post(this.objectUrl(taskId) + 'submit_draft/', submitData, config);
};
suppliosApi.workflowTask.get_form_data = function (taskId, config) {
  return splAxios.get(this.objectUrl(taskId) + 'get_form_data/', config);
};
suppliosApi.workflowTask.call_transition = function (taskId, postData, config) {
  return splAxios.post(this.objectUrl(taskId) + 'call_transition/', postData, config);
};
// WorkflowFormVersion
suppliosApi.workflowFormVersion.get_admin_builder = function (fvId, config) {
  return splAxios.get(this.objectUrl(fvId) + 'get_admin_builder/', config);
};
suppliosApi.workflowFormVersion.update_admin_builder = function (fvId, updateData, config) {
  return splAxios.patch(this.objectUrl(fvId) + 'update_admin_builder/', updateData, config);
};
// ScorecardSystem
suppliosApi.scorecardSystem.get_available_vendors = function (scorecardSystemId, config) {
  return splAxios.get(this.objectUrl(scorecardSystemId) + 'get_available_vendors/', config);
};
suppliosApi.scorecardSystem.get_scorecards_for_vendor = function (scorecardSystemId, config) {
  // this takes a lot of query_params, but do that all in the particular vue component so you can keep all of it together
  // then just pass it into config here
  return splAxios.get(this.objectUrl(scorecardSystemId) + 'get_scorecards_for_vendor/', config);
};
suppliosApi.scorecardSystem.get_scorecards_for_period = function (scorecardSystemId, config) {
  // takes query_params for 'period' and optionally vendor_ids
  return splAxios.get(this.objectUrl(scorecardSystemId) + 'get_scorecards_for_period/', config);
};
suppliosApi.scorecardSystem.get_summary_scorecards_for_vendor = function (config) {
  // pass in same query params as get_scorecards_for_vendor - but no need for scorecardSystemId here!
  // THIS IS FOR DEMO PURPOSES AS OF MARCH 21 2023
  return splAxios.get(this.generalUrl + 'get_summary_scorecards_for_vendor/', config);
};
suppliosApi.scorecardSystem.get_available_periods_nested = function (scorecardSystemId, config) {
  // pass desc=true in query params to get in descending order (just outer list)
  return splAxios.get(this.objectUrl(scorecardSystemId) + 'get_available_periods_nested/', config);
};
suppliosApi.scorecardSystem.get_available_periods_flat = function (scorecardSystemId, config) {
  // pass desc=true in query params to get in descending order
  return splAxios.get(this.objectUrl(scorecardSystemId) + 'get_available_periods_flat/', config);
};
suppliosApi.scorecardSystem.get_system_details = function (scorecardSystemId, config) {
  return splAxios.get(this.objectUrl(scorecardSystemId) + 'get_system_details/', config);
};
suppliosApi.scorecardSystem.get_summary_system_details = function (scorecardSystemId, config) {
  // ONLY FOR DEMO PURPOSES AS OF MARCH 21 2023
  return splAxios.get(this.generalUrl + 'get_summary_system_details/', config);
};
suppliosApi.scorecardSystem.get_quality_system_for_vendor = function (vendorId, config) {
  // put vendorId in query_params as 'vendor'
  const axiosConfig = config ?? {};
  const params = axiosConfig['params'] ?? {};
  params['vendor'] = vendorId;
  axiosConfig['params'] = params;
  return splAxios.get(this.generalUrl + 'get_quality_system_for_vendor/', axiosConfig);
};
suppliosApi.scorecardSystem.list_systems_for_vendor = function (vendorId, config) {
  // put vendorId in query_params as 'vendor'
  const axiosConfig = config ?? {};
  const params = axiosConfig['params'] ?? {};
  params['vendor'] = vendorId;
  axiosConfig['params'] = params;
  return splAxios.get(this.generalUrl + 'list_systems_for_vendor/', axiosConfig);
};
// QualityAnalytics
suppliosApi.qualityAnalytics.list_options = function (config) {
  // includes DWF Specs and new QualityIssue class
  return splAxios.get(this.generalUrl + 'list_options/', config);
};
suppliosApi.qualityAnalytics.get_items = function (wfSpecId, config) {
  return splAxios.get(this.objectUrl(wfSpecId) + 'get_items/', config);
};
suppliosApi.qualityAnalytics.get_columns = function (wfSpecId, config) {
  return splAxios.get(this.objectUrl(wfSpecId) + 'get_columns/', config);
};
suppliosApi.qualityAnalytics.get_combined = function (wfSpecId, config) {
  return splAxios.get(this.objectUrl(wfSpecId) + 'get_combined/', config);
};

// ONBOARDING
suppliosApi.onboarding.list_for_vendor_internal = function (vendor_id, config) {
  const axiosConfig = config || {};
  const params = axiosConfig.params ?? {};
  params['vendor_id'] = vendor_id;
  axiosConfig['params'] = params;
  return splAxios.get(this.generalUrl + 'list_for_vendor_internal/', axiosConfig);
};

// Product / Item
/* suppliosApi.product.listSideload = function(config) {
    return splAxios.get(this.generalUrl + '?include[]=category.*', config);
}; */
suppliosApi.item.getItemSetupOptions = function (config) {
  return splAxios.get(this.generalUrl + 'get_item_setup_options/', config);
}; // for populating select boxes like ProductCategory, etc.
suppliosApi.item.get_item_quote_history = function (itemId, config) {
  return splAxios.get(this.objectUrl(itemId) + 'get_item_quote_history/', config);
};
suppliosApi.item.getFiles = function (itemId, config) {
  return splAxios.get(this.objectUrl(itemId) + 'get_files/', config);
};
suppliosApi.item.get_or_create_by_part_number = function (postData, config) {
  return splAxios.post(this.generalUrl + 'get_or_create_by_part_number/', postData, config);
};
// OBJECT GRID ENDPOINTS FOR item/Product
// suppliosApi.item.get_grid_meta = function ({ include, exclude }) {
//   // FOLLOWS SAME ENDPOINT INTERFACE AS FLOW OBJECT ONE
//   const queryParams = {};
//   if (include) {
//     queryParams['include_fields'] = include;
//   }
//   if (exclude) {
//     queryParams['exclude_fields'] = exclude;
//   }
//   const config = { params: queryParams };
//   return splAxios.get(this.generalUrl + 'get_grid_meta/', config);
// };
suppliosApi.item.provide_grid_meta = function ({ include, exclude, fixedFilterKwargs }) {
  // NEW VERSION OF GRID META - using POST instead of GET, and sending more data
  // calling it fixedFilterKwargs because this is different from the filterModel from the ag-grid
  const postData = {
    include_fields: include || [],
    exclude_fields: exclude || [],
    fixed_filter_kwargs: fixedFilterKwargs || {},
  };
  return splAxios.post(this.generalUrl + 'provide_grid_meta/', postData);
};
suppliosApi.item.provide_grid_data = function ({ gridRequest, include, exclude, fixedFilterKwargs }) {
  // FOLLOWS SAME ENDPOINT INTERFACE AS FLOW OBJECT ONE
  // postData should be the ag-grid request object - query params have include/exclude fields
  // calling it fixedFilterKwargs because this is different from the filterModel from the ag-grid
  // FOR NOW: we merge fixedFilterKwargs into the gridRequest, then pop off in backend... its our thing, not an ag-grid thing
  // TODO - nest things in postData better, so we can have more than just ag-grid stuff at top level
  const postData = { ...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {} };
  const queryParams = {};
  if (include) {
    queryParams['include_fields'] = include;
  }
  if (exclude) {
    queryParams['exclude_fields'] = exclude;
  }
  const config = { params: queryParams };
  return splAxios.post(this.generalUrl + 'provide_grid_data/', postData, config);
};
suppliosApi.item.download_object_grid_excel = function ({ gridRequest, include, exclude, visibleFields, fixedFilterKwargs }) {
  return downloadObjectGridExcel(splAxios, this.generalUrl + 'download_object_grid_excel/', {gridRequest, include, exclude, visibleFields, fixedFilterKwargs});
};
// CompanyProduct
suppliosApi.companyProduct.provide_grid_meta = function ({ include, exclude, fixedFilterKwargs }) {
  const postData = {
    include_fields: include || [],
    exclude_fields: exclude || [],
    fixed_filter_kwargs: fixedFilterKwargs || {},
  };
  return splAxios.post(this.generalUrl + 'provide_grid_meta/', postData);
};
suppliosApi.companyProduct.provide_grid_data = function ({ gridRequest, include, exclude, fixedFilterKwargs }) {
  const postData = { ...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {} };
  const queryParams = {};
  if (include) {
    queryParams['include_fields'] = include;
  }
  if (exclude) {
    queryParams['exclude_fields'] = exclude;
  }
  const config = { params: queryParams };
  return splAxios.post(this.generalUrl + 'provide_grid_data/', postData, config);
};
suppliosApi.companyProduct.download_object_grid_excel = function ({ gridRequest, include, exclude, visibleFields, fixedFilterKwargs }) {
  return downloadObjectGridExcel(splAxios, this.generalUrl + 'download_object_grid_excel/', {gridRequest, include, exclude, visibleFields, fixedFilterKwargs});
};

// RFX
suppliosApi.rfx.get_transitions = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_transitions/', config);
};
suppliosApi.rfx.call_transition = function (rfxId, postData, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'call_transition/', postData, config);
};
suppliosApi.rfx.get_actions_for_tu = function (rfxId, config) {
  // MUST PASS IN 'quicklink_slug' in config.params with quicklink slug value to enable showing actions for quicklinks
  return splAxios.get(this.objectUrl(rfxId) + 'get_actions_for_tu/', config);
};
suppliosApi.rfx.get_detail_internal = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_detail_internal/', config);
};
suppliosApi.rfx.get_detail_external = function (rfxId, config) {
  // MUST PASS IN 'quicklink_slug' in config.params with quicklink slug value if there is NO TU, and its a quicklink view
  return splAxios.get(this.objectUrl(rfxId) + 'get_detail_external/', config);
};
suppliosApi.rfx.get_detail_external_with_response = function (rfxId, config) {
  // MUST PASS IN 'quicklink_slug' in config.params with quicklink slug value if there is NO TU, and its a quicklink view
  return splAxios.get(this.objectUrl(rfxId) + 'get_detail_external_with_response/', config);
};
suppliosApi.rfx.updateQuicklinkStatus = function (rfxId, updateData, config) {
  return splAxios.patch(this.objectUrl(rfxId) + 'update_quicklink_status/', updateData, config);
};
suppliosApi.rfx.getQuicklinkStatus = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_quicklink_status/', config);
};
suppliosApi.rfx.regenerateQuicklink = function (rfxId, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'regenerate_quicklink/', config);
};
suppliosApi.rfx.listCriteria = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'list_criteria/', config);
};
suppliosApi.rfx.updateCriteria = function (rfxId, criteriaListData, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'update_criteria/', criteriaListData, config);
};
suppliosApi.rfx.updateCriteriaSettings = function (rfxId, updateData, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'update_criteria_settings/', updateData, config);
};
suppliosApi.rfx.getResponses = function (rfxId, config) {
  // gets list of rfx responses using list serializer
  return splAxios.get(this.objectUrl(rfxId) + 'get_responses/', config);
}; // might have duplicate of this up above!
suppliosApi.rfx.manageInvitesData = function (rfxId, config) {
  // gets various data for Rfx & related responses needed for managing invites
  return splAxios.get(this.objectUrl(rfxId) + 'manage_invites_data/', config);
};
suppliosApi.rfx.createInvitedResponse = function (rfxId, inviteData, config) {
  // for posting new invites
  return splAxios.post(this.objectUrl(rfxId) + 'create_invited_response/', inviteData, config);
};
suppliosApi.rfx.get_possible_bulk_invite_vendors = function (rfxId, config) {
  // NOTE - this is used for bulk invites, AND for the search endpoint for single invites
  // you will have name passed in in config.params -- gets used in filter in backend
  return splAxios.get(this.objectUrl(rfxId) + 'get_possible_bulk_invite_vendors/', config);
};
suppliosApi.rfx.bulk_create_invited_response = function (rfxId, bulkData, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'bulk_create_invited_response/', bulkData, config);
};
suppliosApi.rfx.list_available_for_invites = function (config) {
  // list of all RFXs with sm_state can_create_invites=True
  return splAxios.get(this.generalUrl + 'list_available_for_invites/', config);
};
suppliosApi.rfx.company_bulk_invite_rfx = function (postData, config) {
  // for bulk inviting a single company to many RFX
  return splAxios.post(this.generalUrl + 'company_bulk_invite_rfx/', postData, config);
};
suppliosApi.rfx.manage_responses_list = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'manage_responses_list/', config);
};
suppliosApi.rfx.getDashboardData = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_dashboard_data/', config);
};
suppliosApi.rfx.getSetupConfigData = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_setup_config_data/', config);
}; // used for getting initial data to manage rfx config options during setup
suppliosApi.rfx.updateSetupConfigData = function (rfxId, updateData, config) {
  return splAxios.patch(this.objectUrl(rfxId) + 'update_setup_config_data/', updateData, config);
}; // used for updating main RFX setup data
suppliosApi.rfx.getRfxSetupOptions = function (config) {
  return splAxios.get(this.generalUrl + 'get_rfx_setup_options/', config);
}; // gets options for select boxes used in forms and list filters - in RfxCreateModal, RfxList, etc.
suppliosApi.rfx.getBulkMessages = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_bulk_messages/', config);
};
suppliosApi.rfx.createBulkMessage = function (rfxId, messageData, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'create_bulk_message/', messageData, config);
};
// not used - checked April 10 2022
//suppliosApi.rfx.getRfxVendors = function(rfxId, config) {
//    return splAxios.get(this.objectUrl(rfxId) + 'get_rfx_vendors/', config);
//};
suppliosApi.rfx.getRfxTenantUsers = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_rfx_tenant_users/', config);
};
suppliosApi.rfx.addRfxTenantUser = function (rfxId, rfxTenantUserData, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'add_rfx_tenant_user/', rfxTenantUserData, config);
};
suppliosApi.rfx.removeRfxTenantUser = function (rfxId, rfxTenantUserId, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'remove_rfx_tenant_user/', rfxTenantUserId, config);
};
/* suppliosApi.rfx.getInitialDataFormCompare = function(rfxId, config) {
    return splAxios.get(this.objectUrl(rfxId) + 'get_initial_data_form_compare/', config);
};
suppliosApi.rfx.getFormsDataForGrid = function(rfxId, config) {
    return splAxios.get(this.objectUrl(rfxId) + 'get_forms_data_for_grid/', config);
}; */
suppliosApi.rfx.compare_grid_get_initial_data = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'compare_grid_get_initial_data/', config);
};
suppliosApi.rfx.changeRfxTenantUserRole = function (rfxId, rfxTenantUserData, config) {
  return splAxios.patch(this.objectUrl(rfxId) + 'change_rfx_tenant_user_role/', rfxTenantUserData, config);
};
suppliosApi.rfx.checkGoLive = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'check_go_live/', config);
}; // check if this RFX is ready to go live
suppliosApi.rfx.launchEvent = function (rfxId, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'launch_event/', config);
}; // post to this (without any data) to launch RFX (set status to open), should be in draft mode before doing this
suppliosApi.rfx.update_invite_settings = function (rfxId, settingsData, config) {
  return splAxios.patch(this.objectUrl(rfxId) + 'update_invite_settings/', settingsData, config);
};
suppliosApi.rfx.getBulkReminderList = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_bulk_reminder_list/', config);
};
suppliosApi.rfx.sendBulkReminder = function (rfxId, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'send_bulk_reminder/', null, config);
};
suppliosApi.rfx.getFiles = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_files/', config);
};
suppliosApi.rfx.get_file_bundle_url = function (rfxId, config) {
  // just returns { url } - then download programmatically on front-end
  return splAxios.get(this.objectUrl(rfxId) + 'get_file_bundle_url/', config);
};
suppliosApi.rfx.getInvitesList = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_invites_list/', config);
};
suppliosApi.rfx.getInvitedListings = function (config) {
  return splAxios.get(this.generalUrl + 'get_invited_listings/', config);
}; // get all RFX objs that current TU is invited to, or submitted response to
suppliosApi.rfx.getPortalListings = function (config) {
  return splAxios.get(this.generalUrl + 'get_portal_listings/', config);
};
suppliosApi.rfx.getPublicListings = function (config) {
  return splAxios.get(this.generalUrl + 'get_public_listings/', config);
};
suppliosApi.rfx.createPortalResponse = function (rfxId, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'create_portal_response/', null, config);
}; // for external TUs to create RfxResponse for open/portal listings
suppliosApi.rfx.listRfxCompanyToDos = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'list_rfx_company_todos/', config);
};
suppliosApi.rfx.createRfxCompanyToDo = function (rfxId, data, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'create_rfx_company_todo/', data, config);
};
// add clone_rfx_company_todo endpoint
suppliosApi.rfx.updateRfxCompanyToDoList = function (rfxId, data, config) {
  // for BULK UPDATE of things like position & required only, not for nested linked_models
  return splAxios.post(this.objectUrl(rfxId) + 'update_rfx_company_todo_list/', data, config);
};
suppliosApi.rfx.get_sourcing_workflow_runs = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_sourcing_workflow_runs/', config);
};
suppliosApi.rfx.get_products = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_products/', config);
};
suppliosApi.rfx.list_rfx_company_todos_external_preview = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'list_rfx_company_todos_external_preview/', config);
};
suppliosApi.rfx.get_all_rfx_related_files = function (rfxId, config) {
  return splAxios.get(this.objectUrl(rfxId) + 'get_all_rfx_related_files/', config);
};
suppliosApi.rfx.create_all_rfx_related_files_bundle_url = function (rfxId, config) {
  return splAxios.post(this.objectUrl(rfxId) + 'create_all_rfx_related_files_bundle_url/', null, config);
};

// Sourcing Request Related APIs - maybe not needed after moving to FlowObject for sourcing request

suppliosApi.sourcingRequest.get_sourcing_request = function (sourcingRequestId, config) {
  return splAxios.get(this.objectUrl(sourcingRequestId) + 'get_detail_view/', config);
};

suppliosApi.sourcingRequest.get_sourcing_request_setup_options = function (config) {
  return splAxios.get(this.generalUrl + 'get_sourcing_request_setup_options/', config);
};

suppliosApi.sourcingRequest.get_files = function (sourcingRequestId, config) {
  return splAxios.get(this.objectUrl(sourcingRequestId) + 'get_files/', config);
};

suppliosApi.sourcingRequest.get_assignies_list = function (sourcingRequestId, config) {
  return splAxios.get(this.objectUrl(sourcingRequestId) + 'get_assignies_list/', config);
};

suppliosApi.sourcingRequest.create_rfx_from_sourcing_request = function (sourcingRequestId, params, config) {
  return splAxios.post(this.objectUrl(sourcingRequestId) + 'create_rfx_from_sourcing_request/', params, config);
};

// RFX RESPONSE - the non-D-REST version
/* suppliosApi.rfxResponse.getPSheetReply = function(responsePk, pSheetPk, config) {
    return splAxios.get(this.objectUrl(responsePk) + 'get_psheet_reply/?psheet=' + pSheetPk.toString(), config);
}; */
/* suppliosApi.rfxResponse.getFormiumReply = function(responseId, formId, config) {  // for new type of FormiumReply
    return splAxios.get(this.objectUrl(responseId) + 'get_formium_reply/?form_id=' + formId.toString(), config);
}; */
suppliosApi.rfxResponse.get_detail_internal = function (responseId, config) {
  // this REPLACED the normal 'retrieve' view -- its the retrieve view for internal users, just to be explicit
  return splAxios.get(this.objectUrl(responseId) + 'get_detail_internal/', config);
};
suppliosApi.rfxResponse.get_transitions = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'get_transitions/', config);
};
suppliosApi.rfxResponse.call_transition = function (responseId, postData, config) {
  return splAxios.post(this.objectUrl(responseId) + 'call_transition/', postData, config);
};
suppliosApi.rfxResponse.updateInvitedResponse = function (responseId, respData, config) {
  return splAxios.put(this.objectUrl(responseId) + 'update_invited_response/', respData, config);
};
/* suppliosApi.rfxResponse.retrieveAsVendor = function(responseId, config) {
    return splAxios.get(this.objectUrl(responseId) + 'retrieve_as_vendor/', config);
};  // REMOVE */
suppliosApi.rfxResponse.get_invited_tus = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'get_invited_tus/', config);
};
suppliosApi.rfxResponse.getCompanyTuList = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'get_company_tu_list/', config);
};
suppliosApi.rfxResponse.getCompanyContacts = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'get_company_contacts/', config);
};
suppliosApi.rfxResponse.createAndAddInvitedTu = function (responseId, tuData, config) {
  return splAxios.post(this.objectUrl(responseId) + 'create_and_add_invited_tu/', tuData, config);
};
suppliosApi.rfxResponse.addInvitedTu = function (responseId, invitedTuId, config) {
  let postData = { invitee: invitedTuId };
  return splAxios.post(this.objectUrl(responseId) + 'add_invited_tu/', postData, config);
};
suppliosApi.rfxResponse.removeInvitedTu = function (responseId, invitedTuId, config) {
  let postData = { invitee: invitedTuId };
  return splAxios.post(this.objectUrl(responseId) + 'remove_invited_tu/', postData, config);
};
suppliosApi.rfxResponse.listRfxCompanyToDosInternal = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'list_rfx_company_todos_internal/', config);
};
suppliosApi.rfxResponse.listRfxCompanyToDosExternal = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'list_rfx_company_todos_external/', config);
};
suppliosApi.rfxResponse.checkCanCreateSubmission = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'check_can_create_submission/', config);
};
suppliosApi.rfxResponse.createNewSubmission = function (responseId, data, config) {
  return splAxios.post(this.objectUrl(responseId) + 'create_new_submission/', data, config);
};
suppliosApi.rfxResponse.list_submissions = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'list_submissions/', config);
};
suppliosApi.rfxResponse.download_submission = function (responseId, submissionId, config) {
  // submissionId is added as a query param - but we'll do it here for convenience
  // this returns an .xlsx file as a download/attachment
  let axiosConfig = config ?? {};
  const queryParams = axiosConfig?.params ?? {}; // this will include any existing params, if you have any
  queryParams['submission_id'] = submissionId;
  axiosConfig.params = queryParams; // set it back on original object
  return splAxios.get(this.objectUrl(responseId) + 'download_submission/', axiosConfig);
};
suppliosApi.rfxResponse.setStarred = function (responseId, isStarredBoolean, config) {
  return splAxios.post(this.objectUrl(responseId) + 'set_starred/', { is_starred: isStarredBoolean }, config);
};
suppliosApi.rfxResponse.getScoreSheets = function (responsePk, config) {
  return splAxios.get(this.objectUrl(responsePk) + 'get_score_sheets/', config);
};
suppliosApi.rfxResponse.createScoreSheet = function (responsePk, scoreSheetData, config) {
  return splAxios.post(this.objectUrl(responsePk) + 'create_score_sheet/', scoreSheetData, config);
};
suppliosApi.rfxResponse.list_for_company = function (companyId, config) {
  const finalConfig = config ?? {};
  const params = finalConfig.params ?? {};
  params['id'] = companyId; // add companyId to query_params - expected by backend
  finalConfig.params = params;
  return splAxios.get(this.generalUrl + 'list_for_company/', finalConfig);
};
suppliosApi.rfxResponse.needs_attention_external_status = function (responseId, config) {
  return splAxios.get(this.objectUrl(responseId) + 'needs_attention_external_status/', config);
};
suppliosApi.rfxResponse.vendor_response_list_setup_data = function (config) {
  return splAxios.get(this.generalUrl + 'vendor_response_list_setup_data/', config);
};
// RfxCompanyToDo
suppliosApi.rfxCompanyToDo.get_external_preview_data = function (rfxCompanyToDoId, config) {
  return splAxios.get(this.objectUrl(rfxCompanyToDoId) + 'get_external_preview_data/', config);
};
suppliosApi.rfxCompanyToDo.deleteTodo = function (rfxCompanyToDoId, postData, config) {
  return splAxios.post(this.objectUrl(rfxCompanyToDoId) + 'delete_todo/', postData, config);
};
suppliosApi.rfxCompanyToDo.getOrCreateReply = function (rfxCompanyToDoId, rfxResponseId, config) {
  let postData = { response_id: rfxResponseId };
  return splAxios.post(this.objectUrl(rfxCompanyToDoId) + 'get_or_create_reply/', postData, config);
};
suppliosApi.rfxCompanyToDo.deleteDraftReply = function (rfxCompanyToDoId, replyId, config) {
  let postData = { reply_id: replyId };
  return splAxios.post(this.objectUrl(rfxCompanyToDoId) + 'delete_draft_reply/', postData, config);
};
suppliosApi.rfxCompanyToDo.getCompareGridData = function (rfxCompanyToDoId, config) {
  return splAxios.get(this.objectUrl(rfxCompanyToDoId) + 'get_compare_grid_data/', config);
};
suppliosApi.rfxCompanyToDo.clone = function (rfxCompanyToDoId, overrideData, config) {
  return splAxios.post(this.objectUrl(rfxCompanyToDoId) + 'clone/', overrideData, config);
};

// RFX AWARD
suppliosApi.rfxAward.create_for_rfx_response = function (rfxResponseId, config) {
  return splAxios.post(this.generalUrl + 'create_for_rfx_response/', { rfx_response_id: rfxResponseId }, config);
};

// RfxResponseScoreSheet
suppliosApi.rfxResponseScoreSheet.getDataForScoring = function (scoreSheetPk, config) {
  return splAxios.get(this.objectUrl(scoreSheetPk) + 'get_data_for_scoring/', config);
};
suppliosApi.rfxResponseScoreSheet.saveScoringData = function (scoreSheetPk, scoreSheetData, config) {
  return splAxios.post(this.objectUrl(scoreSheetPk) + 'save_scoring_data/', scoreSheetData, config);
};

// Vendors
suppliosApi.vendorStandard.deactivate = function (companyId, config) {
  // soft delete, limited only to superadmins
  return splAxios.post(this.objectUrl(companyId) + 'deactivate/', null, config);
};
suppliosApi.vendorStandard.list_options = function (config) {
  // non-paginated list of { id, name }
  return splAxios.get(this.generalUrl + 'list_options/', config);
};
suppliosApi.vendorStandard.listContacts = function (vendorId, config) {
  return splAxios.get(this.objectUrl(vendorId) + 'list_contacts/', config);
};
suppliosApi.vendorStandard.addContact = function (vendorId, newContactData, config) {
  return splAxios.post(this.objectUrl(vendorId) + 'add_contact/', newContactData, config);
};
suppliosApi.vendorStandard.get_rfx_response_history = function (companyId, config) {
  return splAxios.get(this.objectUrl(companyId) + 'get_rfx_response_history/', config);
};
suppliosApi.vendorStandard.createLocation = function (companyId, locationData, config) {
  return splAxios.post(this.objectUrl(companyId) + 'create_location/', locationData, config);
};
suppliosApi.vendorStandard.updateLocation = function (companyId, locationData, config) {
  // we pull out the location ID from locationData in the view
  return splAxios.post(this.objectUrl(companyId) + 'update_location/', locationData, config);
};
suppliosApi.vendorStandard.listLocations = function (companyId, config) {
  return splAxios.get(this.objectUrl(companyId) + 'list_locations/', config);
};
suppliosApi.vendorStandard.setPrimaryLocation = function (companyId, data, config) {
  return splAxios.post(this.objectUrl(companyId) + 'set_primary_location/', data, config);
};
suppliosApi.vendorStandard.getFiles = function (companyId, config) {
  return splAxios.get(this.objectUrl(companyId) + 'get_files/', config);
};
suppliosApi.vendorStandard.update_company_tags = function (companyId, tagData, config) {
  // uses the non-resource endpont for company right now, but uses same serializer as CompanyTag resource endpoint
  return splAxios.post(this.objectUrl(companyId) + 'update_company_tags/', tagData, config);
};

suppliosApi.vendorStandard.create_note = function (companyId, noteData) {
  return splAxios.post(this.objectUrl(companyId) + 'notes/', { title: noteData.title, body: noteData.body });
};

suppliosApi.vendorStandard.edit_note = function (noteId, companyId, noteData) {
  return splAxios.patch(this.objectUrl(companyId) + `notes/?node_id=${noteId}`, { title: noteData.title, body: noteData.body });
};

suppliosApi.vendorStandard.get_note_permissions = function (companyId) {
  return splAxios.get(this.objectUrl(companyId) + 'notes/get-permissions');
};

suppliosApi.vendorStandard.get_notes = function (companyId) {
  return splAxios.get(this.objectUrl(companyId) + 'notes/');
};

suppliosApi.vendorStandard.get_notes_files = function (companyId, noteId) {
  return splAxios.get(this.objectUrl(companyId) + `notes/files/?note_id=${noteId}`);
};

suppliosApi.vendorStandard.delete_note = function (noteId, companyId) {
  return splAxios.delete(this.objectUrl(companyId) + `notes/?note_id=${noteId}`);
};

suppliosApi.vendorStandard.edit_note = function (noteId, companyId, data) {
  return splAxios.patch(this.objectUrl(companyId) + `notes/?note_id=${noteId}`, { title: data.title, body: data.body });
};
// suppliosApi.vendorStandard.get_grid_meta = function ({ include, exclude }) {
//   // FOLLOWS SAME ENDPOINT INTERFACE AS FLOW OBJECT ONE
//   // TODO - change to use provide_grid_meta endpoint!  with fixed_filter_kwargs
//   const queryParams = {};
//   if (include) {
//     queryParams['include_fields'] = include;
//   }
//   if (exclude) {
//     queryParams['exclude_fields'] = exclude;
//   }
//   const config = { params: queryParams };
//   return splAxios.get(this.generalUrl + 'get_grid_meta/', config);
// };
suppliosApi.vendorStandard.provide_grid_meta = function ({ include, exclude, fixedFilterKwargs }) {
  // NEW VERSION OF GRID META - using POST instead of GET, and sending more data
  // calling it fixedFilterKwargs because this is different from the filterModel from the ag-grid
  const postData = {
    include_fields: include || [],
    exclude_fields: exclude || [],
    fixed_filter_kwargs: fixedFilterKwargs || {},
  };
  return splAxios.post(this.generalUrl + 'provide_grid_meta/', postData);
};
suppliosApi.vendorStandard.provide_grid_data = function ({ gridRequest, include, exclude, fixedFilterKwargs }) {
  // FOLLOWS SAME ENDPOINT INTERFACE AS FLOW OBJECT ONE
  // postData should be the ag-grid request object - query params have include/exclude fields
  // calling it fixedFilterKwargs because this is different from the filterModel from the ag-grid
  // FOR NOW: we merge fixedFilterKwargs into the gridRequest, then pop off in backend... its our thing, not an ag-grid thing
  // TODO - nest things in postData better, so we can have more than just ag-grid stuff at top level
  const postData = { ...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {} };
  const queryParams = {};
  if (include) {
    queryParams['include_fields'] = include;
  }
  if (exclude) {
    queryParams['exclude_fields'] = exclude;
  }
  const config = { params: queryParams };
  return splAxios.post(this.generalUrl + 'provide_grid_data/', postData, config);
};

// TAGS
suppliosApi.companyTagCategory.get_tags = function (categoryId, config) {
  // this uses the NEW ResourceViewsetEndpoint - which has this.axios - axios instance baked in
  return this.axios.get(this.objectUrl(categoryId) + 'get_tags/', config);
};

// COMPANY LOCATIONS
suppliosApi.companyLocation.list_for_map = function (config) {
  // TODO - add query params for filtering by company, by tags, by bounding box, etc.
  return splAxios.get(this.generalUrl + 'list_for_map/', config);
};
suppliosApi.companyLocation.provide_grid_meta = function ({ include, exclude, fixedFilterKwargs }) {
  const postData = {
    include_fields: include || [],
    exclude_fields: exclude || [],
    fixed_filter_kwargs: fixedFilterKwargs || {},
  };
  return splAxios.post(this.generalUrl + 'provide_grid_meta/', postData);
};
suppliosApi.companyLocation.provide_grid_data = function ({ gridRequest, include, exclude, fixedFilterKwargs }) {
  const postData = { ...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {} };
  const queryParams = {};
  if (include) {
    queryParams['include_fields'] = include;
  }
  if (exclude) {
    queryParams['exclude_fields'] = exclude;
  }
  const config = { params: queryParams };
  return splAxios.post(this.generalUrl + 'provide_grid_data/', postData, config);
};
suppliosApi.companyLocation.download_object_grid_excel = function ({ gridRequest, include, exclude, visibleFields, fixedFilterKwargs }) {
  return downloadObjectGridExcel(splAxios, this.generalUrl + 'download_object_grid_excel/', {gridRequest, include, exclude, visibleFields, fixedFilterKwargs});
};

// COMPANY SITES (use_vendor_sites tenants) -- same grid endpoints as companyLocation
suppliosApi.companySite.list_for_map = function (config) {
  // same response shape as companyLocation.list_for_map -- used by VendorLocationMap
  return splAxios.get(this.generalUrl + 'list_for_map/', config);
};
suppliosApi.companySite.setPrimary = function (siteId, config) {
  return splAxios.post(this.objectUrl(siteId) + 'set_primary/', {}, config);
};
// per-site contacts (ObjectRoles / ObjectTenantUsers) -- used by CompanySitesManager
suppliosApi.companySite.getObjectRoles = function (siteId, config) {
  return splAxios.get(this.objectUrl(siteId) + 'get_object_roles/', config);
};
suppliosApi.companySite.getObjectTus = function (siteId, config) {
  return splAxios.get(this.objectUrl(siteId) + 'get_object_tus/', config);
};
suppliosApi.companySite.setRoleTus = function (siteId, roleId, tuIds, config) {
  return splAxios.post(this.objectUrl(siteId) + 'set_role_tus/', { role_id: roleId, tu_ids: tuIds }, config);
};
suppliosApi.companySite.provide_grid_meta = function ({ include, exclude, fixedFilterKwargs }) {
  const postData = {
    include_fields: include || [],
    exclude_fields: exclude || [],
    fixed_filter_kwargs: fixedFilterKwargs || {},
  };
  return splAxios.post(this.generalUrl + 'provide_grid_meta/', postData);
};
suppliosApi.companySite.provide_grid_data = function ({ gridRequest, include, exclude, fixedFilterKwargs }) {
  const postData = { ...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {} };
  const queryParams = {};
  if (include) {
    queryParams['include_fields'] = include;
  }
  if (exclude) {
    queryParams['exclude_fields'] = exclude;
  }
  const config = { params: queryParams };
  return splAxios.post(this.generalUrl + 'provide_grid_data/', postData, config);
};
suppliosApi.companySite.download_object_grid_excel = function ({ gridRequest, include, exclude, visibleFields, fixedFilterKwargs }) {
  return downloadObjectGridExcel(splAxios, this.generalUrl + 'download_object_grid_excel/', {gridRequest, include, exclude, visibleFields, fixedFilterKwargs});
};

// MESSAGING
suppliosApi.messageThread.retrieveDynamic = function (threadId, config) {
  return splAxios.get(this.objectUrl(threadId) + 'retrieve_dynamic/', config);
};
suppliosApi.messageThread.postNewMessage = function (threadId, msgData, config) {
  return splAxios.post(this.objectUrl(threadId) + 'post_new_message/', msgData, config);
};
suppliosApi.messageThread.modifyNotifyMembers = function (threadId, data, config) {
  return splAxios.post(this.objectUrl(threadId) + 'modify_notify_members/', data, config);
};
suppliosApi.messageThread.modifyOwnSubscription = function (threadId, notifyBoolean, config) {
  let postData = { notify: notifyBoolean };
  return splAxios.post(this.objectUrl(threadId) + 'modify_own_subscription/', postData, config);
};
suppliosApi.messageThread.list_unread = function (config) {
  return splAxios.get(this.generalUrl + 'list_unread/', config);
};
suppliosApi.coreMessage.createWithFullResponse = function (msgData, config) {
  return splAxios.post(this.generalUrl + 'create_with_full_response/', msgData, config); // a custom create that returns object using retrieve serializer
};

// CONTACTS (External TU) - shares some base endpoints with Internal TU
suppliosApi.contact.sendInviteReminder = function (tuId, config) {
  return splAxios.post(this.objectUrl(tuId) + 'send_invite_reminder/', null, config);
};
suppliosApi.contact.inviteToPortal = function (tuId, inviteData, config) {
  return splAxios.post(this.objectUrl(tuId) + 'invite_to_portal/', inviteData, config);
};
suppliosApi.contact.removePortalAccess = function (tuId, data, config) {
  return splAxios.post(this.objectUrl(tuId) + 'remove_portal_access/', data, config);
};
suppliosApi.contact.deactivate = function (tuId, data, config) {
  // there is no data to send, but since its a POST, axios takes it as an arg
  return splAxios.post(this.objectUrl(tuId) + 'deactivate/', data, config);
};
suppliosApi.contact.tenantRoleOptions = function (config) {
  // Oct 2026 - TenantRoles a supplier contact can hold: [{id, slug, name, description, is_default}] -- external requesters only get the roles external admins may assign
  return splAxios.get(this.generalUrl + 'tenant_role_options/', config);
};
suppliosApi.contact.provide_grid_meta = function({include, exclude, fixedFilterKwargs}) {
  const postData = {
      include_fields: include || [], 
      exclude_fields: exclude || [],
      fixed_filter_kwargs: fixedFilterKwargs || {},
  };
  return splAxios.post(this.generalUrl + 'provide_grid_meta/', postData);
}

suppliosApi.contact.provide_grid_data = function ({ gridRequest, include, exclude, fixedFilterKwargs }) {
  const postData = {...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {}};
  const queryParams = {};
  if (include) {
    queryParams['include_fields'] = include;
  }
  if (exclude) {
    queryParams['exclude_fields'] = exclude;
  }
  const config = { params: queryParams };
  return splAxios.post(this.generalUrl + 'provide_grid_data/', postData, config);
}
suppliosApi.contact.download_object_grid_excel = function ({ gridRequest, include, exclude, visibleFields, fixedFilterKwargs }) {
  return downloadObjectGridExcel(splAxios, this.generalUrl + 'download_object_grid_excel/', {gridRequest, include, exclude, visibleFields, fixedFilterKwargs});
};


// Internal TU
suppliosApi.internalTu.sendInviteReminder = function (tuId, config) {
  return splAxios.post(this.objectUrl(tuId) + 'send_invite_reminder/', null, config);
};
suppliosApi.internalTu.removePortalAccess = function (tuId, data, config) {
  return splAxios.post(this.objectUrl(tuId) + 'remove_portal_access/', data, config);
};
suppliosApi.internalTu.list_portal_users = function (config) {
  // USE THIS IN SELECTS / DROPDOWNS, ETC... CAN PASS QUERY PARAMS FOR SEARCH/FILTER/ETC... PERMS RESTRICTED TO INTERNAL TUS
  // NO LONGER -- IT IS NOT PAGINATED!  and you probably want .list_assignable_users() instead
  return splAxios.get(this.generalUrl + 'list_portal_users/', config);
};
suppliosApi.internalTu.list_assignable_users = function (config) {
  // use this in selects! not paginated, gets all ASSIGNABLE TUs (internal)
  return splAxios.get(this.generalUrl + 'list_assignable_users/', config);
};
suppliosApi.internalTu.deactivate = function (tuId, data, config) {
  // there is no data to send, but since its a POST, axios takes it as an arg
  return splAxios.post(this.objectUrl(tuId) + 'deactivate/', data, config);
};
// tuEmailIssues -- the viewset for just managing email issues for TenantUsers
suppliosApi.tuEmailIssues.list_with_events = function (config) {
  // new list endpoint that has data on all the failing events also
  return splAxios.get(this.generalUrl + 'list_with_events/', config);
};
suppliosApi.tuEmailIssues.clear_email_issues = function (tuId, data, config) {
  return splAxios.post(this.objectUrl(tuId) + 'clear_email_issues/', data, config);
};
suppliosApi.tuEmailIssues.deactivate = function (tuId, data, config) {
  return splAxios.post(this.objectUrl(tuId) + 'deactivate/', data, config);
};

// TenantUserNotes -- the 'id' here is the TU id, not the note id
suppliosApi.tuNotes.list_for_tu = function (tuId, config) {
  return splAxios.get(this.objectUrl(tuId) + 'list_notes/', config);
};
suppliosApi.tuNotes.create_for_tu = function (tuId, noteData, config) {
  return splAxios.post(this.objectUrl(tuId) + 'create_note/', noteData, config);
};
suppliosApi.tuNotes.update_for_tu = function (tuId, noteData, config) {
  // 'id' of the NOTE must be in the noteData
  return splAxios.post(this.objectUrl(tuId) + 'update_note/', noteData, config);
};
suppliosApi.tuNotes.delete_for_tu = function (tuId, noteId, config) {
  let noteData = { id: noteId };
  // 'id' of the NOTE must be in the noteData  (that's it)
  return splAxios.post(this.objectUrl(tuId) + 'delete_note/', noteData, config);
};

// DocRequest & DocRequestReply
suppliosApi.docRequestReply.retrieveInternal = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'retrieve_internal/', config);
};

// QuoteSheet & QuoteSheetReply
suppliosApi.quoteSheet.bulkUpdate = function (quoteSheetId, data, config) {
  return splAxios.put(this.objectUrl(quoteSheetId) + 'bulk_update/', data, config);
};
suppliosApi.quoteSheet.getReplies = function (quoteSheetId, config) {
  // pass in list of reply IDs (reply) or RfxResponse IDs (rfx_response) or Company IDs (company)
  // should be in config.params (each with reply=''&reply='')
  return splAxios.get(this.objectUrl(quoteSheetId) + 'get_replies/', config);
};
suppliosApi.quoteSheet.list_templates = function (config) {
  return splAxios.get(this.generalUrl + 'list_templates/', config);
};
suppliosApi.quoteSheetReply.vendorUpdate = function (quoteSheetReplyId, data, config) {
  return splAxios.put(this.objectUrl(quoteSheetReplyId) + 'vendor_update/', data, config);
};
suppliosApi.quoteSheetReply.retrieveInternal = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'retrieve_internal/', config);
};
suppliosApi.quoteSheetReply.getMultipleReplies = function (config) {
  // list of reply IDs should be in config.params (each with reply=''&reply='')
  return splAxios.get(this.generalUrl + 'get_multiple_replies/', config);
};
suppliosApi.quoteSheetReply.get_currency_data_for_selection = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'get_currency_data_for_selection/', config);
};
suppliosApi.quoteSheetReply.set_quoted_currency = function (replyId, data, config) {
  // data should be {currency: 'USD'} or similar
  return splAxios.post(this.objectUrl(replyId) + 'set_quoted_currency/', data, config);
};
// SMARTFORMS - SmartFormTemplate, SmartForm, SmartFormReply
suppliosApi.smartForm.get_possible_child_forms = function (formId, config) {
  return splAxios.get(this.objectUrl(formId) + 'get_possible_child_forms/', config);
};
suppliosApi.smartForm.get_possible_child_form_templates = function (formId, config) {
  return splAxios.get(this.objectUrl(formId) + 'get_possible_child_form_templates/', config);
};
suppliosApi.smartForm.create_child_form = function (formId, postData, config) {
  return splAxios.post(this.objectUrl(formId) + 'create_child_form/', postData, config);
};
suppliosApi.smartForm.get_for_setup = function (formId, config) {
  return splAxios.get(this.objectUrl(formId) + 'get_for_setup/', config);
};
suppliosApi.smartForm.calculate_values = function (formId, postData, config) {
  return splAxios.post(this.objectUrl(formId) + 'calculate_values/', postData, config);
};
suppliosApi.smartForm.check_setup_data = function (formId, postData, config) {
  return splAxios.post(this.objectUrl(formId) + 'check_setup_data/', postData, config);
};
suppliosApi.smartForm.save_form_setup = function (formId, postData, config) {
  return splAxios.post(this.objectUrl(formId) + 'save_form_setup/', postData, config);
};
suppliosApi.smartForm.get_all_field_types = function (config) {
  // not a detail view
  return splAxios.get(this.generalUrl + 'get_all_field_types/', config);
};
suppliosApi.smartForm.get_or_create_draft_reply = function (formId, config) {
  // for external TUs to get DRAFT reply
  return splAxios.get(this.objectUrl(formId) + 'get_or_create_draft_reply/', config);
};
suppliosApi.smartForm.list_child_forms = function (formId, config) {
  return splAxios.get(this.objectUrl(formId) + 'list_child_forms/', config);
};
suppliosApi.smartForm.download_empty_form = function (formId, config) {
  // also on smartFormReply endpoint, different perms on backend
  return splAxios.get(this.objectUrl(formId) + 'download_empty_form/', config);
};
suppliosApi.smartForm.download_reply = function (formId, replyId, config) {
  // internal only - returns an .xlsx file as a download/attachment
  let axiosConfig = { responseType: 'blob', ...(config ?? {}) };  // blob is important for file downloads
  axiosConfig.params = { ...(axiosConfig.params ?? {}), reply_id: replyId };
  return splAxios.get(this.objectUrl(formId) + 'download_reply/', axiosConfig);
};
suppliosApi.smartForm.download_replies = function (formId, replyIds, config) {
  // internal only - returns an .xlsx file as a download/attachment
  // replyIds is optional (array) - if empty, backend uses latest submitted reply from each vendor
  let axiosConfig = { responseType: 'blob', ...(config ?? {}) };  // blob is important for file downloads
  axiosConfig.params = { ...(axiosConfig.params ?? {}) };
  if (replyIds && replyIds.length) {
    axiosConfig.params.reply_ids = replyIds;
  }
  return splAxios.get(this.objectUrl(formId) + 'download_replies/', axiosConfig);
};
// SmartFormReply
suppliosApi.smartFormReply.get_for_submission = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'get_for_submission/', config);
};
suppliosApi.smartFormReply.get_for_review = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'get_for_review/', config);
};
suppliosApi.smartFormReply.get_markdown = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'get_markdown/', config);
};
suppliosApi.smartFormReply.get_markdown_text_file = function (replyId) {  // as a file attachment (.txt)
  const axiosConfig = { responseType: 'blob' }; // important for file downloads -- or responseType: 'arraybuffer' ???
  return splAxios.get(this.objectUrl(replyId) + 'get_markdown_text_file/', axiosConfig);
};
suppliosApi.smartFormReply.get_markdown_pdf_file = function (replyId) {  // as a file attachment (.pdf)
  const axiosConfig = { responseType: 'blob' }; // important for file downloads -- or responseType: 'arraybuffer' ??? 
  return splAxios.get(this.objectUrl(replyId) + 'get_markdown_pdf_file/', axiosConfig);
};
suppliosApi.smartFormReply.calculate_values = function (replyId, postData, config) {
  return splAxios.post(this.objectUrl(replyId) + 'calculate_values/', postData, config);
};
suppliosApi.smartFormReply.save_reply_data = function (replyId, replyData, config) {
  return splAxios.post(this.objectUrl(replyId) + 'save_reply_data/', replyData, config);
};
suppliosApi.smartFormReply.list_child_forms = function (replyId, config) {
  return splAxios.get(this.objectUrl(replyId) + 'list_child_forms/', config);
};
suppliosApi.smartFormReply.download_empty_form = function (replyId, config) {
  // also on smartForm endpoint, different perms on backend
  return splAxios.get(this.objectUrl(replyId) + 'download_empty_form/', config);
};
suppliosApi.smartFormReply.list_for_product_ids = function (productIds) {
  const axiosConfig = { params: { product_ids: productIds } };
  return splAxios.get(this.generalUrl + 'list_for_product_ids/', axiosConfig);
};
suppliosApi.smartFormReply.list_for_rfx = function (rfxId, config) {
  return splAxios.get(this.generalUrl + 'list_for_rfx/', { params: { rfx_id: rfxId }, ...config });
};
// SmartForm builders - internal - not at /api/...
suppliosApi.smartFormBuilder = {
  getForm: function (formId, config) {
    // formId is passed in query params
    // KEEP AS REFERENCE - IT WORKS!  const axiosConfig = {...config, params: {id: formId}, headers: {'X-Supplios-Tenant-Sub': 'sub2'}};
    const axiosConfig = { ...config, params: { id: formId } };
    return splAxiosBare.get('/smartforms/api/smartform_json_builder/', axiosConfig);
  },
  updateForm: function (updateData, config) {
    // just pass entire update object as POST data - we get the form ID from there
    return splAxiosBare.post('/smartforms/api/smartform_json_builder/', updateData, config);
  },
  get_field_types: function (config) {
    return splAxiosBare.get('/smartforms/api/smartform_field_types/', config);
  },
};
// DOC HUB
suppliosApi.dochubResource.create_new_version = function (resourceId, versionData, config) {
  return splAxios.post(this.objectUrl(resourceId) + 'create_new_version/', versionData, config);
};
suppliosApi.dochubResource.change_status = function (resourceId, postData, config) {
  /// must pass the 'status' in postData
  return splAxios.post(this.objectUrl(resourceId) + 'change_status/', postData, config);
};
suppliosApi.dochubResource.list_resources_and_groups = function (only_published) {
  const config = only_published ? { params: { only_published: true } } : {}; // only add it to query params if its true
  return splAxios.get(this.generalUrl + 'list_resources_and_groups/', config);
};
suppliosApi.dochubResource.get_group_options = function (config) {
  return splAxios.get(this.generalUrl + 'get_group_options/', config);
};
suppliosApi.dochubResource.get_by_slug = function (slug) {
  return splAxios.get(this.generalUrl + 'get_by_slug/', { params: { slug: slug } });
};
// DocHub Versions
suppliosApi.dochubResourceVersion.get_file_bundle_url = function (versionId, config) {
  return splAxios.get(this.objectUrl(versionId) + 'get_file_bundle_url/', config);
};
// DocHub group manager
suppliosApi.dochubGroupManage.enum_options = function (config) {
  return splAxios.get(this.generalUrl + 'enum_options/', config);
};
suppliosApi.dochubGroupManage.list_resources_and_groups = function (config) {
  return splAxios.get(this.generalUrl + 'list_resources_and_groups/', config);
};
suppliosApi.dochubGroupManage.update_group_sort_order = function (postData, config) {
  return splAxios.post(this.generalUrl + 'update_group_sort_order/', postData, config);
};
suppliosApi.dochubGroupManage.update_resource_sorting = function (postData, config) {
  return splAxios.post(this.generalUrl + 'update_resource_sorting/', postData, config);
};

// COMPLIANCE
// suppliosApi.compSubStd.get_submissions_for_vendor = function (config) {  // use VCI instead
//   return splAxios.get(this.generalUrl + 'get_submissions_for_vendor/', config);
// };
// suppliosApi.compSubStd.get_submissions = function (config) {  // use VCI instead
//   return splAxios.get(this.generalUrl + 'get_submissions/', config);
// };
suppliosApi.compSubStd.create_one_off_vendor_comp_item = function (postData, config) {
  return splAxios.post(this.generalUrl + 'create_one_off_vendor_comp_item/', postData, config);
};
suppliosApi.compItemStd.list_comp_items_for_manage = function (config) {
  return splAxios.get(this.generalUrl + 'list_comp_items_for_manage/', config);
};
suppliosApi.compItemStd.get_create_meta = function (config) {
  return splAxios.get(this.generalUrl + 'get_create_meta/', config);
};
suppliosApi.compItemStd.create_comp_item = function (postData, config) {
  return splAxios.post(this.generalUrl + 'create_comp_item/', postData, config);
};
// suppliosApi.compItemStd.get_unrestricted_item_types = function (config) {
//   // OLD - remove
//   return splAxios.get(this.generalUrl + 'get_unrestricted_item_types/', config);
// };
suppliosApi.compItemStd.get_comp_items_can_add_for_vendor = function (vendorId) {
  const config = { params: { vendor_id: vendorId } };
  return splAxios.get(this.generalUrl + 'get_comp_items_can_add_for_vendor/', config);
};
suppliosApi.compItemStd.get_item = function (itemId, config) {
  return splAxios.get(this.objectUrl(itemId) + 'get_item/', config);
};
suppliosApi.compItemStd.list_template_files = function (itemId, config) {
  return splAxios.get(this.objectUrl(itemId) + 'list_template_files/', config);
};
suppliosApi.compItemStd.get_expire_action_specs = function (itemId, config) {
  return splAxios.get(this.objectUrl(itemId) + 'get_expire_action_specs/', config);
};
suppliosApi.compItemStd.create_expire_action_spec = function (itemId, postData, config) {
  return splAxios.post(this.objectUrl(itemId) + 'create_expire_action_spec/', postData, config);
};
suppliosApi.compItemStd.update_expire_action_spec = function (itemId, postData, config) {
  return splAxios.put(this.objectUrl(itemId) + 'update_expire_action_spec/', postData, config);
};
suppliosApi.compItemStd.delete_expire_action_spec = function (itemId, postData, config) {
  // pass EAS ID in postData
  return splAxios.post(this.objectUrl(itemId) + 'delete_expire_action_spec/', postData, config);
};
suppliosApi.compItemStd.list_vendor_comp_items = function ({ vendor_ids, comp_item_ids, with_submissions, only_expiring, only_in_progress, include_empty, with_eas, with_tasks } = {}) {
  const axiosConfig = { params: { vendor_ids, comp_item_ids, with_submissions, only_expiring, only_in_progress, include_empty, with_eas, with_tasks } };
  console.log('list_vendor_comp_items axiosConfig', axiosConfig);
  return splAxios.get(this.generalUrl + 'list_vendor_comp_items/', axiosConfig);
};
suppliosApi.compItemStd.get_status_grid_data = function (config) {
  return splAxios.get(this.generalUrl + 'get_status_grid_data/', config);
};
suppliosApi.compItemStd.get_req_status_grid_data = function ({ vendorIds, compItemIds } = {}) {
  const axiosConfig = {};
  if (compItemIds) {
    axiosConfig.params = { comp_item_ids: compItemIds };
  }
  if (vendorIds) {
    axiosConfig.params = { ...axiosConfig.params, vendor_ids: vendorIds };
  }
  return splAxios.get(this.generalUrl + 'get_req_status_grid_data/', axiosConfig);
};
suppliosApi.compItemStd.refresh_status_grid_data = function (vendorId) {
  const config = { params: { vendor_id: vendorId } };
  return splAxios.get(this.generalUrl + 'refresh_status_grid_data/', config);
};
suppliosApi.compItemStd.get_comp_item_vendor_status_list = function (compItemId, vendorIds) {
  const axiosConfig = {};
  if (vendorIds) {
    axiosConfig.params = { vendor_ids: vendorIds };
  }
  return splAxios.get(this.objectUrl(compItemId) + 'get_comp_item_vendor_status_list/', axiosConfig);
};
suppliosApi.compItemStd.get_comp_item_vendor_status_list_meta = function (compItemId) {
  return splAxios.get(this.objectUrl(compItemId) + 'get_comp_item_vendor_status_list_meta/');
};
suppliosApi.compItemStd.get_visibility = function (compItemId, config) {
  return splAxios.get(this.objectUrl(compItemId) + 'get_visibility/', config);
};
suppliosApi.compItemStd.update_visibility = function (compItemId, postData, config) {
  return splAxios.patch(this.objectUrl(compItemId) + 'update_visibility/', postData, config);
};
// OVERRIDES & Requirements - via the CompItem viewset for perms
suppliosApi.compItemStd.get_override_info_for_vendor = function (compItemId, vendorId) {
  const axiosConfig = { params: { vendor_id: vendorId } };
  return splAxios.get(this.objectUrl(compItemId) + 'get_override_info_for_vendor/', axiosConfig);
};
suppliosApi.compItemStd.set_override_for_vendor = function (compItemId, overrideData, config) {
  // overrideData should include { vendor_id, status, comment }
  return splAxios.post(this.objectUrl(compItemId) + 'set_override_for_vendor/', overrideData, config);
};
suppliosApi.compItemStd.clear_override_for_vendor = function (compItemId, vendorId, config) {
  const postData = { vendor_id: vendorId };
  return splAxios.post(this.objectUrl(compItemId) + 'clear_override_for_vendor/', postData, config);
};
suppliosApi.compItemStd.update_req_rules = function (compItemId, postData, config) {
  return splAxios.post(this.objectUrl(compItemId) + 'update_req_rules/', postData, config);
};
suppliosApi.compItemStd.get_req_rules_status = function (compItemId, config) {
  return splAxios.get(this.objectUrl(compItemId) + 'get_req_rules_status/', config);
};
suppliosApi.compItemStd.get_current_object_role_state = function (compItemId, config) {
  return splAxios.get(this.objectUrl(compItemId) + 'get_current_object_role_state/', config);
};
suppliosApi.compItemStd.handle_comp_item_otu_change = function (compItemId, postData, config) {
  return splAxios.post(this.objectUrl(compItemId) + 'handle_comp_item_otu_change/', postData, config);
};
suppliosApi.compItemStd.create_comp_item_object_role = function (compItemId, postData, config) {
  // postData should be { name, description, allow_any_internal }
  return splAxios.post(this.objectUrl(compItemId) + 'create_comp_item_object_role/', postData, config);
};
suppliosApi.compItemStd.get_comp_sub_node_config_data = function (compItemId, config) {
  return splAxios.get(this.objectUrl(compItemId) + 'get_comp_sub_node_config_data/', config);
};
suppliosApi.compItemStd.update_comp_sub_flow_config = function (compItemId, postData, config) {
  return splAxios.post(this.objectUrl(compItemId) + 'update_comp_sub_flow_config/', postData, config);
};
suppliosApi.compItemStd.disable_supplier_submissions = function (compItemId, config) {
  return splAxios.post(this.objectUrl(compItemId) + 'disable_supplier_submissions/', null, config);
};
// COMP ITEM BULK UPLOADS
suppliosApi.compItemStd.list_comp_item_meta_for_bulk_upload = function (config) {
  return splAxios.get(this.generalUrl + 'list_comp_item_meta_for_bulk_upload/', config);
};
suppliosApi.compItemStd.list_vendors_for_comp_item_bulk_upload = function (compItemId, config) {
  return splAxios.get(this.objectUrl(compItemId) + 'list_vendors_for_comp_item_bulk_upload/', config);
};
suppliosApi.compItemStd.comp_subs_bulk_upload = function (compItemId, compSubList, config) {
  const postData = { items: compSubList };
  return splAxios.post(this.objectUrl(compItemId) + 'comp_subs_bulk_upload/', postData, config);
};

// Compliance Requirement
suppliosApi.compReqFlow.set_override_for_vendor = function (reqId, overrideData, config) {
  // overrideData should include { vendor_id, status, comment }
  return splAxios.post(this.objectUrl(reqId) + 'set_override_for_vendor/', overrideData, config);
};
suppliosApi.compReqFlow.clear_override_for_vendor = function (reqId, vendorId, config) {
  const postData = { vendor_id: vendorId };
  return splAxios.post(this.objectUrl(reqId) + 'clear_override_for_vendor/', postData, config);
};
suppliosApi.compReqFlow.update_rules = function (reqId, postData, config) {
  return splAxios.post(this.objectUrl(reqId) + 'update_rules/', postData, config);
};
suppliosApi.compReqFlow.get_rules_status = function (reqId, config) {
  return splAxios.get(this.objectUrl(reqId) + 'get_rules_status/', config);
};
suppliosApi.compReqFlow.get_override_info_for_vendor = function (reqId, vendorId) {
  const axiosConfig = { params: { vendor_id: vendorId } };
  return splAxios.get(this.objectUrl(reqId) + 'get_override_info_for_vendor/', axiosConfig);
};
// suppliosApi.compReqFlow.get_top_level_data = function (reqId, config) {
//   return splAxios.get(this.objectUrl(reqId) + 'get_top_level_data/', config);
// };
// suppliosApi.compReqFlow.update_top_level_data = function (reqId, updateData, config) {
//   return splAxios.post(this.objectUrl(reqId) + 'update_top_level_data/', updateData, config);
// };
// suppliosApi.compReqFlow.update_comp_item_type = function (reqId, updateData, config) {
//   // id is the CompReq ID, updateData is the CompItemType data
//   return splAxios.post(this.objectUrl(reqId) + 'update_comp_item_type/', updateData, config);
// };
// ComplianceItemType
suppliosApi.compItemTypeFlow.get_visibility = function (itemTypeId, config) {
  return splAxios.get(this.objectUrl(itemTypeId) + 'get_visibility/', config);
};
suppliosApi.compItemTypeFlow.update_visibility = function (itemTypeId, postData, config) {
  return splAxios.patch(this.objectUrl(itemTypeId) + 'update_visibility/', postData, config);
};
suppliosApi.vendorCompItemStd.get_item_status = function (vendorCompItemId, config) {
  return splAxios.get(this.objectUrl(vendorCompItemId) + 'get_item_status/', config);
};
suppliosApi.vendorCompItemStd.create_new_submission = function (vendorCompItemId, subData, config) {
  return splAxios.post(this.objectUrl(vendorCompItemId) + 'create_new_submission/', subData, config);
};
suppliosApi.vendorCompItemStd.get_expire_action_specs = function (vendorCompItemId, includeCompItem) {
  // if includeCompItem is true, we will pass it in query params, telling backend to include EAS on the CompItem also
  const queryParams = {};
  if (includeCompItem) {
    queryParams['include_comp_item'] = true;
  }
  const config = { params: queryParams };
  return splAxios.get(this.objectUrl(vendorCompItemId) + 'get_expire_action_specs/', config);
};
suppliosApi.vendorCompItemStd.create_expire_action_spec = function (vendorCompItemId, postData, config) {
  return splAxios.post(this.objectUrl(vendorCompItemId) + 'create_expire_action_spec/', postData, config);
};
suppliosApi.vendorCompItemStd.update_expire_action_spec = function (vendorCompItemId, postData, config) {
  return splAxios.put(this.objectUrl(vendorCompItemId) + 'update_expire_action_spec/', postData, config);
};
suppliosApi.vendorCompItemStd.delete_expire_action_spec = function (vendorCompItemId, postData, config) {
  // pass EAS ID in postData
  return splAxios.post(this.objectUrl(vendorCompItemId) + 'delete_expire_action_spec/', postData, config);
};
// COMP EAS
suppliosApi.compEas.get_eas_options = function (compItemId, vciId) {
  // pass ONE of compItemId or vciId - we will pass them both to backend, but only one can be truthy
  const queryParams = {};
  if (compItemId) {
    queryParams['comp_item_id'] = compItemId;
  }
  if (vciId) {
    queryParams['vci_id'] = vciId;
  }
  const config = { params: queryParams };
  return splAxios.get(this.generalUrl + 'get_eas_options/', config);
};

// EXTRA PERFORMANCE ENDPOINTS
suppliosApi.performanceSystem.list_all_systems = function (config) {
  return splAxios.get(this.generalUrl + 'list_all_systems/', config);
};

// COMPANY PROFILE DATA ENDPOINTS
suppliosApi.companyProfileData.list_contacts = function (companyId, config) {
  return splAxios.get(this.objectUrl(companyId) + 'list_contacts/', config);
};

///////////////////////////////////////////////////////////////////////////
/// TENANT HQ -- for HQ API endpoints from TENANT subdomains (not the central HQ subdomain)

const suppliosTenantHqApi = {
  tenant_users: new GenericViewsetEndpoint({ generalUrl: 'hq-tenant-users/', enabledActions: ['list'] }),
};
suppliosTenantHqApi.tenant_users.send_invite = function (tuId, config) {
  return splAxios.post(this.objectUrl(tuId) + 'send_invite/', null, config);
};
suppliosTenantHqApi.tenant_users.renew_invite = function (tuId, config) {
  return splAxios.post(this.objectUrl(tuId) + 'renew_invite/', null, config);
};
suppliosTenantHqApi.tenant_users.change_tu_email = function (tuId, newEmailAddress, config) {
  const postData = { email: newEmailAddress };
  return splAxios.post(this.objectUrl(tuId) + 'change_tu_email/', postData, config);
};
suppliosTenantHqApi.tenant_users.deactivate = function (tuId, config) {
  return splAxios.post(this.objectUrl(tuId) + 'deactivate/', null, config);
};
suppliosTenantHqApi.tenant_users.clear_email_issues = function (tuId, config) {
  return splAxios.post(this.objectUrl(tuId) + 'clear_email_issues/', null, config);
};
suppliosTenantHqApi.tenant_users.set_external_user_role = function (tuId, roleSlug, config) {
  const postData = { role_slug: roleSlug };
  return splAxios.post(this.objectUrl(tuId) + 'set_external_user_role/', postData, config);
};
// suppliosTenantHqApi.tenant_users.disconnect_tenant_user_and_reinvite = function (tuId, config) {
//   return splAxios.post(this.objectUrl(tuId) + 'disconnect_tenant_user_and_reinvite/', null, config);
// };

// tenantConfig endpoints
suppliosApi.tenantConfig.get_config = function (config) {
  return splAxios.get(this.generalUrl + 'get_config/', config);
};
suppliosApi.tenantConfig.update_config = function (updateData, config) {
  return splAxios.patch(this.generalUrl + 'update_config/', updateData, config);
};

///////////////////////////////////////////////////////////////////////////////
// separate object for HQ API endpoints
// NOTE - if you use relative URLs here, these will only work when being called from hq.supplios.com subdomain
// they are really only intended to be used for stuff from HQ subdomain anyways... but if you need them from tenants subdomain or elsewhere, you'll need to specify the base URL
const suppliosHqApi = {
  tenants: new APIEndpoint('tenants/'), // HQ viewset for Tenants
  smartFormManager: {
    retrieve: function (formId, config) {
      const axiosConfig = { ...config, params: { id: formId } };
      return splAxios.get('smartform_manager/', axiosConfig);
    },
    update: function (updateData, config) {
      // just pass entire update object as POST data
      return splAxios.post('smartform_manager/', updateData, config);
    },
    get_field_types: function (config) {
      return splAxios.get('smartform_field_types/', config);
    },
    create_field: function (fieldData, config) {
      return splAxios.post('smartform_create_field/', fieldData, config);
    },
  },
  smartFormFormulateBuilder: {
    getForm: function (formId, config) {
      // formId is passed in query params
      // KEEP AS REFERENCE - IT WORKS!  const axiosConfig = {...config, params: {id: formId}, headers: {'X-Supplios-Tenant-Sub': 'sub2'}};
      const axiosConfig = { ...config, params: { id: formId } };
      return splAxios.get('smartform_formulate_builder/', axiosConfig);
    },
    updateForm: function (updateData, config) {
      // just pass entire update object as POST data - we get the form ID from there
      return splAxios.post('smartform_formulate_builder/', updateData, config);
    },
    // add others for getting the SCHEMA (live form)
  },
  serviceEmails: {
    send_service_email: function (data, config) {
      return splAxios.post('send_service_email/', data, config);
    },
  },
  // using snake_case to match things in backend, so find/replace is easier
  bulk_upload: {
    url: 'bulk_upload/',
    get_available_models: function (config) {
      return splAxios.get(this.url + 'get_available_models/', config);
    },
    bulk_upload: function (postData, config) {
      // single endpoint for validate/create (different post data)
      return splAxios.post(this.url + 'bulk_upload/', postData, config);
    },
  },
  tenantDataExport: {
    getOptions: function (config) {
      return splAxios.get('tenant_data_export/', config);
    },
    exportModels: function (postData, config) {
      return splAxios.post('tenant_data_export/', postData, config);
    },
  },
  authorization: {
    // ONLY WORKS FROM HQ SUBDOMAIN - part of hq urls
    async updateDefaultAuthorizationData() {
      await splAxios.post('/authorization/update_authz_data/');
    },
    async updateTenantDefaultRoles(tenantId) {
      await splAxios.post('/authorization/update_default_roles_for_tenant/', { tenant_id: tenantId });
    },
  },
};
// EXTRA ACTIONS FOR HQ API
suppliosHqApi.tenants.create_supplios_admin_tu = function (tenantId, postData, config) {
  const createData = postData ?? {}; // no data to post as of Oct 12 2022
  return splAxios.post(this.objectUrl(tenantId) + 'create_supplios_admin_tu/', createData, config);
};
suppliosHqApi.tenants.list_with_admins = function (config) {
  return splAxios.get(this.generalUrl + 'list_with_admins/', config);
};
suppliosHqApi.tenants.download_vendor_export_excel = function (tenantId, config) {
  return splAxios.get(this.objectUrl(tenantId) + 'download_vendor_export_excel/', config);
};

// FORM GETTERS FOR AJAX FORMS

// constructor for generic form endpoint
// skip for now... later if useful (like APIEndpoint)

const splAxiosText = axios.create({
  // only for URLs that don't use router (not through /api/)
  // AND only for URLs that should return HTML, not JSON
  responseType: 'text',
  xsrfCookieName: window?.splData?.CSRF_COOKIE_NAME || 'csrftoken',
  xsrfHeaderName: 'X-CSRFTOKEN',
  headers: { ...(window?.splFixedHeaders || {}) },
});

const suppliosForms = {
  formiumReply: {
    update: {
      // URL PK for update is the FormiumReply PK/ID
      url: function (formiumReplyId) {
        return '/formbuilder/formium-reply/ajax-update/' + formiumReplyId.toString() + '/';
      },
      get: function (formiumReplyId, config) {
        return splAxiosText.get(this.url(formiumReplyId), config);
      },
      post: function (formiumReplyId, formData, config) {
        return splAxiosText.post(this.url(formiumReplyId), formData, config);
      },
    },
  },
};

// HELPER FOR GETTING A RESOURCE API & FlowObjectViewsetEndpoint
const endpointsByResourceName = {};
/**
 * An object that maps model class names to their corresponding API endpoints.
 * @type {Object.<string, ResourceViewsetEndpoint | FlowObjectViewsetEndpoint>}
 */
const flowObjectEndpointsByModelClass = {};
Object.entries(suppliosApi).forEach((nameEndpointPair) => {
  const endpointInst = nameEndpointPair[1];
  if (endpointInst instanceof ResourceViewsetEndpoint) {
    const resourceName = endpointInst.resourceName;
    endpointsByResourceName[resourceName] = endpointInst;
  } else if (endpointInst instanceof FlowObjectViewsetEndpoint) {
    const modelClass = endpointInst.modelClass;
    //const flowSubtype = endpointInst.flowSubtype;  // TODO - use this when you implement it
    flowObjectEndpointsByModelClass[modelClass] = endpointInst;
  }
});

// below is same... but also no intellisense is working....
//const epByResourceName = Object.fromEntries(Object.values(suppliosApi).filter(ep => ep instanceof ResourceViewsetEndpoint).map(ep => [ep.resourceName, ep]));
//const flowObjectEndpointsByModelName = Object.fromEntries(Object.values(suppliosApi).filter(ep => ep instanceof FlowObjectViewsetEndpoint).map(ep => [ep.modelClass, ep]));

const getResourceApi = (resourceName) => {
  if (resourceName in endpointsByResourceName) {
    return endpointsByResourceName[resourceName];
  } else {
    console.error(`No resource endpoint found with name ${resourceName}`);
  }
};

// HELPER FOR GETTING A FlowObjectViewsetEndpoint
// STILL USING THIS INSTEAD OF CONSTRUCTING THEM ALL ON DEMAND
// because you can't figure out (with short time) how to add 'extra' endpoints to them all dynamically
// so we only get the custom one dynamically... the others we declare up front
const getFlowObjectApi = (flowObjectModelClass, flowSubtype) => {
  // flowSubtype is optional -- if the model class doesn't use it, it gets ignored
  console.log(`starting getFlowObjectApi for flowObjectModelClass: ${flowObjectModelClass} and flowSubtype ${flowSubtype}`);
  if (flowObjectModelClass in flowObjectEndpointsByModelClass) {
    console.log(`found already-declared endpoint in api-service for ${flowObjectModelClass}`);
    return flowObjectEndpointsByModelClass[flowObjectModelClass];
  } else {
    console.log(`did NOT find already-declared endpoint in api-service for ${flowObjectModelClass} - constructing using getCustomFlowObjectEndpoint`);
    // are we working with our CustomFlowObject one?  then we need to construct it
    return getCustomFlowObjectEndpoint(flowObjectModelClass, flowSubtype);
  }
};

// TODO - make a convenience class for making calls on a FlowObjectInstance -- FlowObjectInstanceApi
// return things with standardized types for nodes, transitions, etc.

export { getFlowObjectApi, getResourceApi, splAxios, splAxiosBare, splAxiosText, suppliosApi, suppliosApiNew, suppliosForms, suppliosHqApi, suppliosTenantHqApi };
